import {isAdmin, isSelectiveAiDomains} from 'common/HelperFunctions'
import {detectDeviceType} from 'common/helpers/deviceType'
import {
  type OnboardingBatchEvent,
  trackOnboardingEvent,
  trackOnboardingEvents,
} from 'common/helpers/onboardingEventTracking'
import {
  ONBOARDING_SAMPLE_DATA_CLEARED_KEY,
  clearOnboardingSampleDataClearedFlag,
  getOnboardingSampleDataPrefs,
  markSampleCreditsPopupPending,
} from 'common/helpers/onboardingSampleData'
import {onboardingWizardStateKey} from 'common/helpers/onboardingWizardStorage'
import {useSupportUser} from 'common/hooks/useSupportUser'
import {Check, ChevronLeft} from 'lucide-react'
import React, {FC, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useIntl} from 'react-intl'
import {useDispatch, useSelector} from 'react-redux'
import {useLocation, useNavigate, useParams} from 'react-router'
import {toast} from 'react-toastify'
import {RootState} from 'setup'

import {toAbsoluteUrl} from '_metronic/helpers'

import {systemMode, useThemeMode} from '../../../../../_metronic/partials'
import {getBranches} from '../../../../../setup/redux/Actions/Branch/branch'
import {bulkCsvImport} from '../../../../../setup/redux/Actions/CsvImport/csvImport'
import {
  addDefaultSampleCredits,
  getUserPreferences,
  updateUserPreferences,
} from '../../../../../setup/redux/Actions/Dashboard/dashboard'
import {updateSubjectWiseSetting} from '../../../../../setup/redux/Actions/General/settings'
import {markOnboardingComplete} from '../../../../../setup/redux/Actions/Onboarding/completeOnboarding'
import {getOnboardingData} from '../../../../../setup/redux/Actions/Onboarding/getOnboardingData'
import {seedOnboardingSampleData} from '../../../../../setup/redux/Actions/Onboarding/seedSampleData'
import {requestSaveOnboardingProgress} from '../../../../../setup/redux/Actions/Onboarding/teacherAllocationServer'
import OnboardingSampleDataLoader from './components/OnboardingSampleDataLoader'
import OnboardingWelcomeModal from './components/OnboardingWelcomeModal'
import {
  SaveAndFinishLaterAction,
  SaveAndFinishLaterModal,
  type SaveLaterStatus,
  SetupPausedScreen,
} from './components/SaveAndFinishLater'
import Step1Mode from './components/Step1Mode'
import Step2Review from './components/Step2Review'
import Step3AcademicStructure from './components/Step3AcademicStructure'
import Step3SuccessSummary from './components/Step3SuccessSummary'
import Step4Manual from './components/Step4Manual'
import {
  clearStoredCurriculumPrefills,
  readStoredCurriculumPrefills,
  setPlainCurriculumNames,
} from './curriculumPrefill'
import {initialData} from './initialWizardData'
import {
  assignSubjectsSlug,
  assignSubjectsTab,
  assignSubjectsTeachersPhase,
  buildOnboardingPath,
  canonicalStepSlug,
  canonicalSubStepSlug,
  coreSetupSubStepIndex,
  getStepBySlug,
  onboardingPositionName,
  onboardingPositionRank,
  onboardingProgressPercent,
  onboardingStepLabel,
  positionsCompletedByStep,
  setupFieldsPassedBefore,
  stepForNumber,
  stepNumberForSlug,
} from './onboardingSections'
import {clearStoredSchoolSystemDetails} from './schoolSystemDetailsCache'
import {SeedSampleDataResult} from './types/seedSampleData'
import {applyBulkImportResponseToManualData} from './utils/bulkImportIdMerge'
import {
  buildLevelCurriculumLookup,
  curriculumFieldsFor,
  detectLevelCurriculumSupport,
  lookupLevelId,
  lookupSubjectId,
  shouldRequestPlainNames,
} from './utils/levelCurriculum'
import {
  ONBOARDING_SETUP_FIELDS,
  type OnboardingClassesDraft,
  type OnboardingCurriculumConfig,
  type OnboardingSetup,
  extractClassesDraft,
  extractCurriculumConfig,
  extractOnboardingSetup,
  fillSubjectsFromDraft,
  hasRealCurriculum,
  isPlaceholderCurriculumConfig,
  mapOnboardingDataToManualData,
  mergeClassesDraft,
} from './utils/mapOnboardingData'
import {editedPositionsIn, modeSelectionEdited} from './utils/onboardingEdits'
import {
  applyPendingDeletes,
  buildBranchDeletePayload,
  buildLevelDeletePayload,
  consumeDeletedBranches,
  consumeDeletedLevels,
  consumeDeletedSubjects,
} from './utils/pendingDeletes'
import {
  inferResumeFromOnboardingData,
  lostResumePoint,
  readablePreferences,
} from './utils/serverResume'
import {resolveStudentPlacement} from './utils/studentClassMatch'
import {studentRowKey} from './utils/studentsFile'
import {TEACHER_ALLOCATION_V2, allocationServerEnabled} from './utils/teacherAllocationFlow'
import {buildTeachersDraft, teachersDraftToRestore} from './utils/teachersDraft'

export type OnboardingSyncOptions = {
  forceAllocationSync?: boolean
  /**
   * Restrict the sync to these sections (the checkpoint's other sections are not even
   * compared). For a save whose only purpose is to get specific data server-side before a
   * read — a template download — so an unrelated section whose snapshot drifted does not
   * trigger a request of its own.
   */
  sectionsOnly?: IncrementalSectionKey[]
}

/** Options a step component attaches to a wizard-state write (see `updateData`). */
export type UpdateDataOptions = {
  /** The wizard made this write on the user's behalf (a seeded default), not the user. */
  prefill?: boolean
  /**
   * The sub-step slug the write came from, when the step component knows better than the
   * wizard's URL state. Step 4's Add Teachers and Review Teachers screens both write
   * `teachers`, so only the writer can say which position was edited.
   */
  subStep?: string | null
}

export type OnboardingPartialSubmit = (
  fields: any[],
  manualData?: WizardData['manualData'],
  cb?: (manualData?: WizardData['manualData']) => void,
  syncOptions?: OnboardingSyncOptions,
  onError?: (err: any) => void
) => void

export type WizardData = {
  mode: 'class' | 'subject' | null
  /** Institution country picked (or detected) on Setup Basics; drives curriculum recommendations. */
  country: string | null
  importType: 'manual' | 'csv' | null
  manualData: {
    lessonPlanningData: any
    deletedLessonSlots?: string[]
    /** Teacher Allocation: slots whose teacher came from an uploaded file (grid file icon). */
    importedLessonSlots?: string[]
    /** Teacher Allocation: sections removed from this timetable setup (row removal). */
    removedLessonClasses?: string[]
    /** Teacher Allocation: the Add Teachers file hints last pre-filled (see prefillFromAllocationHints). */
    allocationHintsSignature?: string
    // Classes removed in the Review step. Kept so the next bulk-import can send them with
    // action:'delete', otherwise the backend keeps them and they reappear on resume.
    deletedClasses?: Array<{
      grade: string
      section: string
      name: string
      branch?: string
      level: string
      curriculum?: string
      id?: number
      /** Hydrated from GET /onboarding/data (persisted server-side) though it carries no id. */
      persisted?: boolean
    }>
    /**
     * Levels removed on Academic Structure. The bulk-import branch payload only lists the levels
     * that still exist, which leaves the removed one in the tenant DB — and GET /onboarding/data
     * hands it straight back on resume. Sent as `action:'delete'` in a SEPARATE request after
     * the classes request (a level must be emptied before it can go); persisted with the classes
     * draft so a logout before Next cannot lose the queue. A level removed from one branch only
     * (different levels per branch) carries that `branch` and is deleted there only.
     */
    deletedLevels?: Array<{curriculum?: string; name: string; branch?: string}>
    /**
     * Saved branches removed on Institution Setup. Sent as `action:'delete'` in their own
     * request after the classes one (node-api then drops the whole branch: classes, levels,
     * timetable data, sections). Persisted with the classes draft, like `deletedLevels`.
     */
    deletedBranches?: Array<{name: string; id: number}>
    /**
     * Saved subject rows (one per class) of a subject removed from a level. Sent as
     * `action:'delete'` rows in `subjects` on the next sync; the server drops the subject's
     * teacher allocation and the students' links to it with the row.
     */
    deletedSubjects?: Array<{
      id: number
      name: string
      class: string
      branch?: string
      level?: string
      curriculum?: string
    }>
    branches: string[]
    curriculums: string[]
    /**
     * `timetable/curricula` code by curriculum display name. Drives the `timetable/prefill`
     * calls; names typed under "Other" (or picked from the legacy school-systems list) have none.
     */
    curriculumCodes?: Record<string, string>
    /**
     * Catalogue short code by curriculum display name ("Federal Board (FBISE)" → "FBISE").
     * Sent as `curriculum_short_code` on each level and shown beside a level/class name two
     * curricula share. From `timetable/curricula`, the prefill, or resume-data.
     */
    curriculumShortCodes?: Record<string, string>
    /**
     * Names typed under "Other" on Setup Basics, kept apart from the selection. A custom name
     * is in no catalog, so `curriculums` used to be its only anchor and de-selecting it made
     * the pill vanish for good. This list keeps it on the row (unselected, re-selectable) and
     * survives resume via the preferences curriculum config.
     */
    customCurriculums?: string[]
    /**
     * Provenance of the Institution Setup prefill (the prefill's `sources` block: timings,
     * timings_review, session, frequencies, …) so the review page can label non-curated values.
     */
    prefillSources?: Record<string, string>
    /** Working-week note from the prefill (e.g. "Friday is a half day"), shown under Timings. */
    schoolWeekNote?: string
    /**
     * "<country>|<curriculum code>" of the prefill that last seeded Institution Setup ("defaults"
     * when the flat defaults did). A different country or curriculum on the next visit re-seeds
     * session, hours and breaks from that prefill. Persisted with the curriculum config.
     */
    institutionPrefillKey?: string
    levelsSameAcrossBranches: boolean | null
    levelsCount: number
    levels: Array<{name: string}>
    levelsPerBranch?: Record<string, string[]>
    levelsByCurriculum?: Record<
      string,
      {
        // `id` is the Academic Structure drawer's draft id, written back by
        // `applyAcademicStructure` so a reopened level keeps its identity.
        levels: Array<{name: string; id?: string | number}>
        levelsSameAcrossBranches: boolean | null
        levelsPerBranch?: Record<string, string[]>
        levelDetails?: Record<
          string,
          {
            classes: Array<{
              name: string
              subjects: string[]
            }>
          }
        >
      }
    >
    durationName?: string
    durationStartDate?: string
    durationEndDate?: string
    venuesSameForClass?: boolean | null
    venuesAutoCount?: number
    teachersCount: number
    teachers: Array<{
      id?: number
      first_name: string
      last_name: string
      employeeId?: string
      level: string
      /** The level's curriculum, when resume-data says; otherwise resolved from `level`. */
      curriculum?: string
      email: string
      cnic: string
    }>
    subjectIdByNameAndClass?: Record<string, number>
    /** Server branch ids by branch name (bulk-import response). `branches` itself stays names. */
    branchIdByName?: Record<string, number>
    classesCount: number
    classes: Array<{
      id?: number
      grade: string
      section: string
      teacher: string
      name: string
      level: string
      branch?: string
      curriculum?: string
      /**
       * True for a row hydrated from GET /onboarding/data, which returns no class id. Such a
       * row is persisted server-side, so removing it must still produce a delete (the backend
       * resolves a class delete by name + level, the id is optional).
       */
      persisted?: boolean
    }>
    subjectsCount: number
    subjectSelectionsByLevel?: Record<string, string[]>
    subjects: Array<{
      name: string
      curriculum?: string
      level?: string
      branch?: string
      teacher: string
      class: string
      frequency: string
      duration: string
    }>
    studentsCount: number
    students: Array<{
      first_name: string
      last_name: string
      email: string
      cnic: string
      class: string
      registrationNumber: string
      subjects: Array<{name: string; class: string}>
      /** Set once a Students save succeeded with this row: it exists in the school. */
      _saved?: boolean
    }>
    /**
     * Saved students removed on the Students step (the row's trash icon, or Remove on a
     * prefill). Sent with `action:'delete'` on the next Students save, otherwise the backend
     * keeps them: it never reads a student missing from the list as removed, and they come
     * back on resume and in the next prefill template. Persisted with the setup, cleared once
     * the save succeeds.
     */
    deletedStudents?: Array<{
      registrationNumber?: string
      full_name?: string
      id?: number
      class?: string
      branch?: string
      level?: string
      curriculum?: string
    }>
    /**
     * Saved teachers the user removed, sent with `action:'delete'` as soon as they are removed
     * (Step4Manual); whatever that request could not delete goes again with the next Teachers
     * save. The backend finds a teacher to delete by id.
     */
    deletedTeachers?: Array<{id: number; name?: string; employeeId?: string}>
    venuesCount: number
    venues: Array<{name: string}>
    defaultSubjectFrequency?: number
    defaultFrequencyPer?: 'week' | 'day'
    break_timings?: Array<{
      day: string | number
      startTime: string
      endTime: string
    }>
    timings?: Array<{
      day: string
      startTime: string
      endTime: string
      lectureDuration?: number
      breakStartTime?: string
      breakEndTime?: string
      defaultSubjectFrequency?: number
      frequencyPer?: 'week' | 'day'
      /** Subject/class/section relationships found next to the teacher in an uploaded file
       *  (Add Teachers step). Kept so Teacher Allocation can be pre-filled from them. */
      allocationHints?: Array<{subject?: string; class?: string; section?: string}>
      /** Review-only marker: extracted with low confidence, shown with a "check" badge until edited. */
      _needsReview?: boolean
      /** Review-only: name of the uploaded file the row came from. */
      _sourceFile?: string
      /** Review-only: how many lines of the upload were read as this one teacher. */
      _mergedLines?: number
    }>
    manualActiveTab?: string
    classesSectionCount?: string
    classesSectionNomenclature?: string
    classesFlowStep?: number
    timetableBranchOptions?: TimetableOption[]
    timetableLevelOptions?: TimetableOption[]
    timetableDurationOptions?: TimetableOption[]
    selectedTimetableBranchId?: number | null
    selectedTimetableLevelId?: number | null
    selectedTimetableDurationId?: number | null
  }
}

type TimetableOption = {
  id: number
  name: string
  branchId?: number
  levelId?: number
  startDate?: string
  endDate?: string
}

type ManualData = WizardData['manualData']
type IncrementalSectionKey =
  | 'branch'
  | 'duration'
  | 'venues'
  | 'timings'
  | 'teachers'
  | 'classes'
  | 'subjects'
  | 'students'
  | 'lessonPlanning'

const CHECKPOINT_SECTIONS: Record<string, IncrementalSectionKey[]> = {
  levels: ['branch'],
  duration: ['branch', 'duration'],
  timings: ['branch', 'duration', 'venues', 'timings'],
  teachers: ['branch', 'duration', 'venues', 'timings', 'teachers'],
  classes: ['branch', 'duration', 'venues', 'timings', 'teachers', 'classes'],
  students: [
    'branch',
    'duration',
    'venues',
    'timings',
    'teachers',
    'classes',
    'subjects',
    'students',
  ],
  lessonPlanning: [
    'branch',
    'duration',
    'venues',
    'timings',
    'teachers',
    'classes',
    'lessonPlanning',
  ],
}

const SECTION_TO_PAYLOAD_KEYS: Record<
  IncrementalSectionKey,
  Array<keyof ReturnType<typeof buildBulkImportPayload>>
> = {
  branch: ['branch'],
  duration: ['duration'],
  venues: ['venues'],
  timings: ['timings', 'break_timings'],
  teachers: ['teachers'],
  classes: ['classes'],
  subjects: ['subjects'],
  students: ['students'],
  lessonPlanning: ['subjects'],
}

const hasSectionData = (section: IncrementalSectionKey, manualData: ManualData) => {
  switch (section) {
    case 'branch':
      return (manualData.branches ?? []).length > 0 || (manualData.deletedBranches ?? []).length > 0
    case 'duration':
      return (
        (manualData.durationName ?? '').trim().length > 0 ||
        !!manualData.durationStartDate ||
        !!manualData.durationEndDate
      )
    case 'venues':
      return (
        (manualData.venues ?? []).length > 0 ||
        (manualData.venuesAutoCount ?? 0) > 0 ||
        (manualData.venuesSameForClass === true && (manualData.classes ?? []).length > 0)
      )
    case 'timings':
      return (
        (manualData.timings ?? []).length > 0 ||
        ((manualData as any).break_timings ?? []).length > 0
      )
    case 'teachers':
      return (manualData.teachers ?? []).length > 0 || (manualData.deletedTeachers ?? []).length > 0
    case 'classes':
      return (
        (manualData.classes ?? []).length > 0 ||
        ((manualData as any).deletedClasses ?? []).length > 0 ||
        (manualData.deletedLevels ?? []).length > 0
      )
    case 'subjects':
      return (manualData.subjects ?? []).length > 0 || (manualData.deletedSubjects ?? []).length > 0
    case 'students':
      return (manualData.students ?? []).length > 0 || (manualData.deletedStudents ?? []).length > 0
    case 'lessonPlanning':
      return (
        Object.keys((manualData.lessonPlanningData as Record<string, any>) ?? {}).length > 0 ||
        (manualData.deletedLessonSlots ?? []).length > 0
      )
    default:
      return false
  }
}

/**
 * Shape-insensitive view of the subjects rows for the sent-snapshot compare. The rows are
 * rebuilt by two different builders (applyAcademicStructure on Academic Structure, Step4Manual's
 * flattenSubjectsFromSelections on every Step 4 save) with different key orders, and the
 * bulk-import response merges server ids into them — so a plain JSON.stringify never matched
 * and every Step 4 sync re-sent subjects the user had not touched. Ids are left out on purpose:
 * they never reflect a user edit.
 */
const normalizeSubjectsForSnapshot = (subjects: ManualData['subjects'] | undefined): string[] =>
  ((subjects ?? []) as any[])
    .map((subject) =>
      [
        subject?.name,
        subject?.curriculum,
        subject?.level,
        subject?.branch,
        subject?.class,
        subject?.teacher,
        subject?.frequency,
        subject?.duration,
        subject?.action,
      ]
        .map((value) => String(value ?? '').trim())
        .join('|||')
    )
    .sort()

const getSectionSnapshot = (section: IncrementalSectionKey, manualData: ManualData) => {
  switch (section) {
    case 'branch':
      return JSON.stringify({
        branches: manualData.branches ?? [],
        curriculums: manualData.curriculums ?? [],
        levelsByCurriculum: manualData.levelsByCurriculum ?? {},
        // Branch deletes go in their own request after this section's (see syncIncrementalData).
        deletedBranches: manualData.deletedBranches ?? [],
      })
    case 'duration':
      return JSON.stringify({
        durationName: manualData.durationName ?? '',
        durationStartDate: manualData.durationStartDate ?? '',
        durationEndDate: manualData.durationEndDate ?? '',
        // node-api creates a duration row per branch/level from this section, so a newly added
        // branch or level must re-send it; otherwise its break timings fail with
        // "Duration not found for level ... in branch ...".
        branches: manualData.branches ?? [],
        levelsByCurriculum: manualData.levelsByCurriculum ?? {},
      })
    case 'venues':
      return JSON.stringify({
        venuesSameForClass: manualData.venuesSameForClass ?? null,
        venuesAutoCount: manualData.venuesAutoCount ?? 0,
        venues: manualData.venues ?? [],
        // When venuesSameForClass, snapshot is tied to class names so venues re-syncs when classes change
        ...(manualData.venuesSameForClass === true
          ? {
              classNames: (manualData.classes ?? [])
                .map((c: any) => String(c?.name ?? '').trim())
                .filter(Boolean)
                .sort(),
            }
          : {}),
      })
    case 'timings':
      return JSON.stringify({
        timings: manualData.timings ?? [],
        break_timings: (manualData as any).break_timings ?? [],
        // Timings, off days and breaks are stored per duration (one per branch/level), so a new
        // branch or level must re-send them or it is left with none.
        branches: manualData.branches ?? [],
        levelsByCurriculum: manualData.levelsByCurriculum ?? {},
      })
    case 'teachers':
      return JSON.stringify({
        teachers: manualData.teachers ?? [],
        deletedTeachers: manualData.deletedTeachers ?? [],
      })
    case 'classes':
      return JSON.stringify({
        classes: manualData.classes ?? [],
        deletedClasses: (manualData as any).deletedClasses ?? [],
        // Level deletes ride the classes checkpoint (their request follows the classes one).
        deletedLevels: manualData.deletedLevels ?? [],
      })
    case 'subjects':
      return JSON.stringify({
        subjects: normalizeSubjectsForSnapshot(manualData.subjects),
        deletedSubjects: (manualData.deletedSubjects ?? []).map((entry) => entry?.id).sort(),
      })
    case 'students':
      return JSON.stringify({
        students: manualData.students ?? [],
        deletedStudents: manualData.deletedStudents ?? [],
      })
    case 'lessonPlanning':
      return JSON.stringify({
        lessonPlanningData: manualData.lessonPlanningData ?? {},
        deletedLessonSlots: manualData.deletedLessonSlots ?? [],
      })
    default:
      return ''
  }
}

const DAY_TO_NUMBER: Record<string, number> = {
  MONDAY: 1,
  TUESDAY: 2,
  WEDNESDAY: 3,
  THURSDAY: 4,
  FRIDAY: 5,
  SATURDAY: 6,
  SUNDAY: 7,
}

const toDayNumber = (day: unknown): number => {
  if (typeof day === 'number' && day >= 1 && day <= 7) return day
  if (typeof day !== 'string') return 1
  const normalized = day.trim().toUpperCase()
  return DAY_TO_NUMBER[normalized] ?? 1
}

const splitFullName = (fullName: string) => {
  const clean = (fullName || '').trim()
  if (!clean) return {first_name: '', last_name: ''}
  const parts = clean.split(/\s+/)
  return {
    first_name: parts[0] || '',
    last_name: parts.slice(1).join(' '),
  }
}

const normalizeAction = (action: unknown, fallback: 'insert' | 'update' | 'delete' = 'insert') => {
  const normalized = String(action ?? '')
    .trim()
    .toLowerCase()
  if (normalized === 'insert' || normalized === 'update' || normalized === 'delete') {
    return normalized
  }
  return fallback
}

const inferAction = (item: any, fallback: 'insert' | 'update' | 'delete' = 'insert') => {
  if (!item) return fallback
  if (item?.action) return normalizeAction(item.action, fallback)
  return parsePositiveNumber(item?.id) ? 'update' : fallback
}

const addIdIfPresent = (target: Record<string, any>, source: any) => {
  const id = parsePositiveNumber(source?.id)
  if (id) {
    target.id = id
  }
  return target
}

/** The bulk-import request body for `manualData` (exported for tests). */
export const buildBulkImportPayload = (manualData: ManualData) => {
  const rawBranches = (manualData.branches as any[]) ?? []
  const branchNames = rawBranches
    .map((branch) => (typeof branch === 'string' ? branch : branch?.name))
    .map((name) => String(name ?? '').trim())
    .filter(Boolean)

  // Class rows are per BRANCH: with two or more branches every branch has its own
  // "Grade 1-A". A name-only map kept only the last branch's row, so every lesson-planning
  // subject and student resolved to that branch and the other branches' rows collapsed out
  // of the payload. Resolve by (branch, name) first, name-only as the fallback. Used for the
  // lesson-planning subjects only; students go through resolveStudentPlacement, which also
  // keys on level and never guesses between same-named classes.
  // Curriculum on levels: a level is identified by branch + curriculum + name, so every row
  // that names a level also carries its curriculum (display name); levels also carry the
  // catalogue code and short code. Short codes the wizard has not stored yet come from a
  // cached prefill. See utils/levelCurriculum.ts.
  const levelCurricula = buildLevelCurriculumLookup(manualData)
  const cachedShortCodes: Record<string, string> = {}
  Object.entries(readStoredCurriculumPrefills()).forEach(([name, prefill]) => {
    const shortCode = String(prefill?.curriculumShortCode ?? '').trim()
    if (shortCode) cachedShortCodes[name] = shortCode
  })
  const curriculumCodeSource = {
    curriculumCodes: manualData.curriculumCodes,
    curriculumShortCodes: {...cachedShortCodes, ...(manualData.curriculumShortCodes ?? {})},
  }
  const curriculumFields = (curriculum: unknown, withCodes = false) =>
    curriculumFieldsFor(curriculum, curriculumCodeSource, withCodes)
  const curriculumKey = (curriculum: unknown) =>
    String(curriculum ?? '')
      .trim()
      .toLowerCase()

  const classByName = new Map<string, any>()
  const classByBranchAndName = new Map<string, any>()
  const classByBranchCurriculumAndName = new Map<string, any>()
  const branchClassKey = (branchName: any, className: any) =>
    `${String(branchName ?? '').trim()}|||${String(className ?? '').trim()}`
  ;(manualData.classes ?? []).forEach((cls: any) => {
    if (!cls?.name) return
    if (!classByName.has(cls.name)) classByName.set(cls.name, cls)
    const key = branchClassKey(cls?.branch, cls.name)
    if (!classByBranchAndName.has(key)) classByBranchAndName.set(key, cls)
    const curriculum = curriculumKey(cls?.curriculum)
    if (curriculum) {
      const qualifiedKey = `${curriculum}|||${key}`
      if (!classByBranchCurriculumAndName.has(qualifiedKey)) {
        classByBranchCurriculumAndName.set(qualifiedKey, cls)
      }
    }
  })
  const resolveClass = (className: any, branchHint?: any, curriculumHint?: any) => {
    const name = String(className ?? '').trim()
    if (!name) return undefined
    const hint = String(branchHint ?? '').trim()
    const curriculum = curriculumKey(curriculumHint)
    return (
      (hint && curriculum
        ? classByBranchCurriculumAndName.get(`${curriculum}|||${branchClassKey(hint, name)}`)
        : undefined) ??
      (hint ? classByBranchAndName.get(branchClassKey(hint, name)) : undefined) ??
      classByName.get(name)
    )
  }

  // Existing subject rows by "name|||class|||curriculum|||branch" (used when lesson planning
  // assigns teachers). The curriculum and branch are part of the key: two curricula can share a
  // class name, and every branch has its own "Grade 1-A".
  const subjectRowKey = (
    name: unknown,
    className: unknown,
    curriculum: unknown,
    branch: unknown
  ) =>
    `${String(name ?? '').trim()}|||${String(className ?? '').trim()}|||${curriculumKey(curriculum)}|||${String(branch ?? '').trim()}`
  const subjectByNameAndClass = new Map<string, any>()
  ;(manualData.subjects ?? []).forEach((subj: any) => {
    if (!String(subj?.name ?? '').trim() || !String(subj?.class ?? '').trim()) return
    const curriculum = levelCurricula.resolve(subj?.branch, subj?.level, subj?.curriculum)
    subjectByNameAndClass.set(subjectRowKey(subj?.name, subj?.class, curriculum, subj?.branch), subj)
  })

  // Persisted per-class subject IDs from prior API responses (name|||className → subjectId)
  const subjectIdByNameAndClass: Record<string, number> =
    ((manualData as any).subjectIdByNameAndClass as Record<string, number>) ?? {}

  // Persisted className → venueId map from a prior venues API response. When
  // "Classes Stay in the Same Venue" is selected, each class has a dedicated venue
  // ("Venue N (ClassName)"), and the venue's id is sent as roomId on each subject.
  const venueIdByClassName: Record<string, number> =
    ((manualData as any).venueIdByClassName as Record<string, number>) ?? {}

  // Lookup maps for resolving lesson-planning teacher display names to API-format full names.
  // In the teachers API payload, last_name = employeeId, so API full name = first_name + employeeId.
  const teacherByEmployeeId = new Map<string, any>()
  const teacherByDisplayName = new Map<string, any>()
  ;(manualData.teachers ?? []).forEach((t: any) => {
    const empId = String(t?.employeeId ?? '').trim()
    const displayName = `${t?.first_name ?? ''} ${t?.last_name ?? ''}`.trim()
    if (empId && !teacherByEmployeeId.has(empId)) teacherByEmployeeId.set(empId, t)
    if (displayName && !teacherByDisplayName.has(displayName))
      teacherByDisplayName.set(displayName, t)
  })

  const resolveTeacherApiName = (displayName: string, teacherEmployeeId?: string): string => {
    const empIdKey = String(teacherEmployeeId ?? '').trim()
    const teacher =
      (empIdKey ? teacherByEmployeeId.get(empIdKey) : null) ??
      teacherByDisplayName.get(String(displayName ?? '').trim())
    if (!teacher) return String(displayName ?? '').trim()
    // Mirror the teachers payload: first_name carries the full entered name (first + last),
    // last_name carries the employeeId.
    const firstName = [teacher?.first_name, teacher?.last_name]
      .map((s: any) => String(s ?? '').trim())
      .filter(Boolean)
      .join(' ')
    const apiLastName = String(teacher?.employeeId ?? '').trim()
    return [firstName, apiLastName].filter(Boolean).join(' ')
  }

  // The branch's levels with the curriculum each belongs to. The same name under two curricula
  // is two levels.
  const getDerivedLevelsForBranch = (branchName: string) => {
    let levelNames: string[] = []

    if (manualData.levelsByCurriculum) {
      const levels: Array<{name: string; curriculum: string}> = []
      const seen = new Set<string>()
      Object.entries(manualData.levelsByCurriculum).forEach(([curriculum, currData]) => {
        const names =
          currData.levelsSameAcrossBranches === false
            ? (currData.levelsPerBranch?.[branchName] ?? []).filter(Boolean)
            : (currData.levels ?? [])
                .map((l) => l.name)
                .filter((name): name is string => !!name?.trim())
        names.forEach((name) => {
          const key = `${String(name).trim()}|||${curriculumKey(curriculum)}`
          if (seen.has(key)) return
          seen.add(key)
          levels.push({name, curriculum})
        })
      })
      return levels
    } else {
      const perBranchLevels = (manualData.levelsPerBranch?.[branchName] ?? []).filter(Boolean)
      const globalLevelNames = (manualData.levels ?? [])
        .map((level) => level?.name)
        .filter((name): name is string => !!name?.trim())

      if (manualData.levelsSameAcrossBranches === false) {
        levelNames = perBranchLevels
      } else if (globalLevelNames.length > 0) {
        levelNames = globalLevelNames
      } else {
        levelNames = perBranchLevels
      }
    }

    return Array.from(new Set(levelNames)).map(
      (name) => ({name}) as {name: string; curriculum?: string}
    )
  }

  // Persisted branch→level ID mapping from prior API responses
  const branchLevelIdMap: Record<string, Record<string, number>> = ((manualData as any)
    .branchLevelIdMap as Record<string, Record<string, number>>) ?? {}
  const branchIdByName: Record<string, number> = manualData.branchIdByName ?? {}

  const branch = branchNames.map((branchName) => {
    const branchEntry = rawBranches.find((b) => {
      const name = typeof b === 'string' ? b : b?.name
      return String(name ?? '').trim() === branchName
    })
    const branchLevelsRaw =
      Array.isArray((branchEntry as any)?.levels) && (branchEntry as any).levels.length > 0
        ? (branchEntry as any).levels
        : getDerivedLevelsForBranch(branchName)

    // Keyed by name + curriculum: "Primary" under FBISE and under Cambridge are two levels.
    const levelsByName = new Map<string, any>()
    branchLevelsRaw.forEach((lvl: any) => {
      const levelName = String((typeof lvl === 'string' ? lvl : lvl?.name) ?? '').trim()
      if (!levelName) return
      const curriculum = levelCurricula.resolve(
        branchName,
        levelName,
        typeof lvl === 'string' ? undefined : lvl?.curriculum
      )
      const levelKey = `${levelName}|||${curriculumKey(curriculum)}`
      if (!levelsByName.has(levelKey)) {
        const levelPayload: any = {
          name: levelName,
          ...curriculumFields(curriculum, true),
          action: inferAction(lvl, 'insert'),
        }
        addIdIfPresent(levelPayload, lvl)
        // Look up persisted level ID from branchLevelIdMap. A bare-name id is not trusted
        // for a name another curriculum shares: it may be that curriculum's level, and an
        // update by id would move it. Such a level goes as an insert, which the backend
        // matches to the existing level by branch + curriculum + name.
        if (!parsePositiveNumber(levelPayload.id)) {
          const mappedLevelId = lookupLevelId(
            branchLevelIdMap[branchName],
            levelName,
            curriculum,
            levelCurricula.isShared(branchName, levelName)
          )
          if (mappedLevelId) {
            levelPayload.id = mappedLevelId
            levelPayload.action = 'update'
          }
        }
        levelsByName.set(levelKey, levelPayload)
      }
    })

    const branchPayload: any = {
      name: branchName,
      action: inferAction(branchEntry, 'insert'),
      levels: Array.from(levelsByName.values()),
    }
    addIdIfPresent(branchPayload, branchEntry)
    // Branches are kept as names; a synced branch's id lives in branchIdByName.
    if (!parsePositiveNumber(branchPayload.id)) {
      const mappedBranchId = parsePositiveNumber(branchIdByName[branchName])
      if (mappedBranchId) {
        branchPayload.id = mappedBranchId
        if (branchPayload.action === 'insert') branchPayload.action = 'update'
      }
    }
    // The default "Main Branch" (DEFAULT_BRANCH_NAME) always goes as a name-only insert, even
    // once its id is known. node-api renames the DB's "Main Branch" to the first INSERTED
    // branch whenever the insert rows do not include that name - it only looks at inserts - so
    // an update row for it beside a newly added branch made the server rename Main Branch to
    // the new name, rename it back on the update row, insert the new branch again, and report
    // the new name twice (once carrying Main Branch's id). The first-wins id merge then pinned
    // both names to one id and the next sync renamed Main Branch away, leaving only the new
    // branch. The insert path reuses the existing row by name, so nothing is created twice.
    if (branchName === 'Main Branch' && branchPayload.action === 'update') {
      branchPayload.action = 'insert'
      delete branchPayload.id
    }
    return branchPayload
  })

  const primaryBranch =
    branch.find((b: any) => b?.action !== 'delete')?.name ?? branch[0]?.name ?? ''
  const primaryLevelEntry =
    branch
      .find((b: any) => b?.action !== 'delete')
      ?.levels?.find((l: any) => l?.action !== 'delete') ?? branch[0]?.levels?.[0]
  // The curriculum of the fallback level, sent whenever a row falls back to `primaryLevel`.
  const primaryLevelCurriculum: string | undefined = primaryLevelEntry?.curriculum
  const primaryLevel =
    primaryLevelEntry?.name ??
    (manualData.levelsByCurriculum
      ? Object.values(manualData.levelsByCurriculum)
          .flatMap((c) =>
            c.levelsSameAcrossBranches === false
              ? Object.values(c.levelsPerBranch ?? {}).flat()
              : c.levels.map((l) => l.name)
          )
          .filter(Boolean)[0]
      : manualData.levelsPerBranch
        ? Object.values(manualData.levelsPerBranch).flat()[0]
        : '') ??
    ''

  const timings = (manualData.timings ?? []).map((timing: any) => {
    const payload: any = {
      day: toDayNumber(timing?.day),
      endTime: timing?.endTime ?? '',
      lectureTiming: Number(timing?.lectureTiming ?? timing?.lectureDuration ?? 0) || 0,
      startTime: timing?.startTime ?? '',
      action: inferAction(timing, 'insert'),
    }
    addIdIfPresent(payload, timing)
    return payload
  })

  const rawBreakTimings = (manualData as any).break_timings ?? []
  const hasStructuredBreaks = rawBreakTimings.some(
    (entry: any) => Array.isArray(entry?.levels) && entry?.branch
  )

  type BreakSlotPayload = {
    day: number
    startTime: string
    endTime: string
    action: 'insert' | 'update' | 'delete'
    id?: number
  }

  const flatBreaks: BreakSlotPayload[] = [
    ...rawBreakTimings
      .filter((timing: any) => !Array.isArray(timing?.levels))
      .map((timing: any) => {
        const entry: BreakSlotPayload = {
          day: toDayNumber(timing?.day),
          startTime: timing?.startTime ?? '',
          endTime: timing?.endTime ?? '',
          action: inferAction(timing, 'insert'),
        }
        addIdIfPresent(entry as any, timing)
        return entry
      }),
    ...(manualData.timings ?? [])
      .filter((timing: any) => timing?.breakStartTime && timing?.breakEndTime)
      .map((timing: any) => ({
        day: toDayNumber(timing?.day),
        startTime: timing?.breakStartTime ?? '',
        endTime: timing?.breakEndTime ?? '',
        action: 'insert' as const,
      })),
  ]

  const structuredBreaksByBranchLevel = new Map<string, BreakSlotPayload[]>()
  if (hasStructuredBreaks) {
    rawBreakTimings.forEach((branchEntry: any) => {
      const branchName = String(branchEntry?.branch ?? '').trim()
      if (!branchName || !Array.isArray(branchEntry?.levels)) return

      branchEntry.levels.forEach((levelEntry: any) => {
        const levelName = String(levelEntry?.name ?? '').trim()
        if (!levelName) return
        const normalized = (Array.isArray(levelEntry?.breaks) ? levelEntry.breaks : [])
          .map((br: any) => {
            const entry: BreakSlotPayload = {
              day: toDayNumber(br?.day),
              startTime: br?.startTime ?? '',
              endTime: br?.endTime ?? '',
              action: inferAction(br, 'insert'),
            }
            addIdIfPresent(entry as any, br)
            return entry
          })
          .filter((entry: BreakSlotPayload) => {
            if (entry.action === 'delete') return true
            return !!entry.startTime && !!entry.endTime
          })
        const levelCurriculum = curriculumKey(levelEntry?.curriculum)
        structuredBreaksByBranchLevel.set(
          levelCurriculum
            ? `${branchName}|||${levelCurriculum}|||${levelName}`
            : `${branchName}|||${levelName}`,
          normalized
        )
      })
    })
  }

  const break_timings = (() => {
    const branchesWithLevels =
      branch.length > 0
        ? branch
        : [
            {
              name: primaryBranch || 'Main Branch',
              levels: [{name: primaryLevel || 'Primary'}],
            },
          ]

    // Levels go as {name, curriculum}: a bare name cannot tell two "Primary" levels apart.
    return branchesWithLevels.map((branchEntry) => ({
      branch: branchEntry.name,
      levels: (branchEntry.levels ?? []).map((levelEntry: any) => ({
        name: levelEntry.name,
        ...curriculumFields(levelEntry.curriculum),
        breaks:
          (levelEntry.curriculum
            ? structuredBreaksByBranchLevel.get(
                `${branchEntry.name}|||${curriculumKey(levelEntry.curriculum)}|||${levelEntry.name}`
              )
            : undefined) ??
          structuredBreaksByBranchLevel.get(`${branchEntry.name}|||${levelEntry.name}`) ??
          flatBreaks,
      })),
    }))
  })()

  // Levels still present per branch. A de-selected curriculum's level is deleted server-side
  // and its teachers moved to another level, so a teacher row still naming that level must
  // fall back to primaryLevel or processTeachers looks up a level that no longer exists.
  const liveLevelsByBranch = new Map<string, Set<string>>()
  branch.forEach((branchItem: any) => {
    if (branchItem?.action === 'delete') return
    const levelNames = new Set<string>(
      (branchItem?.levels ?? [])
        .filter((levelItem: any) => levelItem?.action !== 'delete')
        .map((levelItem: any) => String(levelItem?.name ?? '').trim())
        .filter(Boolean)
    )
    liveLevelsByBranch.set(String(branchItem?.name ?? '').trim(), levelNames)
  })

  const teachers = (manualData.teachers ?? []).map((teacher: any) => {
    // last_name is reserved for employeeId in the API payload (see note above), so the
    // full entered name (first + last) is sent in first_name to avoid dropping the surname.
    const fullFirstName = [teacher?.first_name, teacher?.last_name]
      .map((s: any) => String(s ?? '').trim())
      .filter(Boolean)
      .join(' ')
    const teacherBranch = teacher?.branch || primaryBranch
    const liveLevels = liveLevelsByBranch.get(String(teacherBranch).trim())
    const teacherLevelName = String(teacher?.level ?? '').trim()
    const keepsOwnLevel = !!teacherLevelName && (!liveLevels || liveLevels.has(teacherLevelName))
    const teacherLevel = keepsOwnLevel ? teacher.level : primaryLevel
    const payload: any = {
      first_name: fullFirstName,
      last_name: teacher?.employeeId ?? '',
      branch: teacherBranch,
      level: teacherLevel,
      ...curriculumFields(
        keepsOwnLevel
          ? levelCurricula.resolve(teacherBranch, teacherLevel, teacher?.curriculum)
          : primaryLevelCurriculum
      ),
      email: teacher?.email ?? '',
      cnic: teacher?.cnic ?? '',
      action: inferAction(teacher, 'insert'),
    }
    addIdIfPresent(payload, teacher)
    return payload
  })
  // Removed saved teachers the immediate delete could not remove: `action:'delete'` by id. One
  // still (or again) on the list is not deleted.
  const presentTeacherIds = new Set(
    (manualData.teachers ?? []).map((t: any) => parsePositiveNumber(t?.id)).filter(Boolean)
  )
  const deletedTeacherIds = new Set<number>()
  ;(manualData.deletedTeachers ?? []).forEach((teacher) => {
    const id = parsePositiveNumber(teacher?.id)
    if (!id || presentTeacherIds.has(id) || deletedTeacherIds.has(id)) return
    deletedTeacherIds.add(id)
    teachers.push({
      id,
      first_name: teacher.name ?? '',
      last_name: teacher.employeeId ?? '',
      action: 'delete',
    })
  })

  // A class row's curriculum: its own, else its level's (when only one curriculum has that
  // level in the branch); a row falling back to primaryLevel takes that level's curriculum.
  const classCurriculum = (cls: any): string | undefined =>
    cls?.level
      ? levelCurricula.resolve(cls?.branch || primaryBranch, cls.level, cls?.curriculum)
      : primaryLevelCurriculum

  let classes = (manualData.classes ?? [])
    .map((cls: any) => {
      const branchName = cls?.branch || primaryBranch
      const levelName = cls?.level || primaryLevel
      const validBranchLevelKeys = new Set<string>()
      branch.forEach((branchItem: any) => {
        const bName = String(branchItem?.name ?? '').trim()
        ;(branchItem?.levels ?? []).forEach((levelItem: any) => {
          const lName = String(levelItem?.name ?? '').trim()
          if (bName && lName) {
            validBranchLevelKeys.add(`${bName}|||${lName}`)
          }
        })
      })

      if (
        validBranchLevelKeys.size > 0 &&
        !validBranchLevelKeys.has(`${branchName}|||${levelName}`)
      ) {
        return null
      }

      const payload: any = {
        grade: cls?.grade ?? '',
        section: cls?.section ?? '',
        teacher: cls?.teacher ?? '',
        name: cls?.name ?? '',
        branch: branchName,
        level: levelName,
        ...curriculumFields(classCurriculum(cls)),
        action: inferAction(cls, 'insert'),
      }
      addIdIfPresent(payload, cls)
      return payload
    })
    .filter(Boolean)

  // Classes the user removed in the Review step. Send them with action:'delete' so the backend
  // actually removes them (otherwise they persist and reappear on resume). A deleted class must go
  // ONLY as delete — so drop its identity from the insert/update list above, then emit the deletes.
  // The curriculum is part of the identity: "Grade 1-A" can exist under two curricula.
  const classIdentityKey = (cls: any) =>
    [
      String(cls?.name ?? '')
        .trim()
        .toLowerCase(),
      String(cls?.branch || primaryBranch)
        .trim()
        .toLowerCase(),
      String(cls?.level || primaryLevel)
        .trim()
        .toLowerCase(),
      curriculumKey(classCurriculum(cls)),
    ].join('|||')

  // A branch queued for delete takes its classes (and their subjects) with it server-side; a
  // separate delete for one of them would only come back as "Class not found for deletion".
  const deletedBranchNames = new Set(
    (manualData.deletedBranches ?? []).map((entry) =>
      String(entry?.name ?? '')
        .trim()
        .toLowerCase()
    )
  )
  const inDeletedBranch = (branchName: unknown) =>
    deletedBranchNames.has(
      String(branchName ?? '')
        .trim()
        .toLowerCase()
    )
  const deletedClassSeen = new Set<string>()
  const deletedClassPayloads = (((manualData as any).deletedClasses as any[]) ?? [])
    .filter((cls: any) => {
      if (inDeletedBranch(cls?.branch)) return false
      // Only classes already persisted by the API can be deleted. A local draft that was
      // added and removed before its first bulk import must never become a delete payload.
      // Rows hydrated from GET /onboarding/data carry no id but are persisted (`persisted`);
      // the backend resolves a class delete by name + level, so the id is optional.
      if (!parsePositiveNumber(cls?.id) && cls?.persisted !== true) return false
      const name = String(cls?.name ?? '').trim()
      if (!name) return false
      const key = classIdentityKey(cls)
      if (deletedClassSeen.has(key)) return false
      deletedClassSeen.add(key)
      return true
    })
    .map((cls: any) => {
      const payload: any = {
        grade: cls?.grade ?? '',
        section: cls?.section ?? '',
        teacher: '',
        name: cls?.name ?? '',
        branch: cls?.branch || primaryBranch,
        level: cls?.level || primaryLevel,
        ...curriculumFields(classCurriculum(cls)),
        action: 'delete',
      }
      addIdIfPresent(payload, cls)
      return payload
    })

  // Deleted wins over insert: a class present in both lists (e.g. it lingered in the current set
  // after deletion) is removed here so it is never sent as insert alongside its delete.
  if (deletedClassSeen.size > 0) {
    classes = (classes as any[]).filter((cls: any) => !deletedClassSeen.has(classIdentityKey(cls)))
  }

  const classesByBranchLevel = new Map<string, any[]>()
  ;(classes as any[]).forEach((cls: any) => {
    const className = String(cls?.name ?? '').trim()
    const branchName = String(cls?.branch ?? '').trim()
    const levelName = String(cls?.level ?? '').trim()
    if (!className || !branchName || !levelName || cls?.action === 'delete') return
    const key = `${branchName}|||${curriculumKey(cls?.curriculum)}|||${levelName}`
    const existing = classesByBranchLevel.get(key) ?? []
    existing.push(cls)
    classesByBranchLevel.set(key, existing)
  })

  // Default frequency/duration from step 2 (used when lesson planning cell has empty values)
  const defaultFreq = manualData.defaultSubjectFrequency
  const defaultPer = manualData.defaultFrequencyPer || 'week'
  const defaultFrequencyStr =
    defaultFreq && defaultFreq >= 1 ? `${defaultFreq}/${defaultPer}` : '1/week'
  const defaultDurationStr = '1'

  // Lesson-planning grid: (class -> subject -> teacher/frequency/duration) cells; merged with the
  // curriculum selections below into the subjects section.
  const lessonPlanData =
    (manualData.lessonPlanningData as
      | Record<string, Record<string, {teacher: string; frequency: string; duration: string}>>
      | undefined) ?? {}
  const deletedSlots = new Set<string>((manualData.deletedLessonSlots as string[]) ?? [])

  type SubjectEntry = {
    name: string
    teacher: string
    class: string
    frequency: string
    duration: string
    branch: string
    level: string
    curriculum?: string
    action: string
    id: number | null
  }
  const slotKey = (cn: string, sn: string) => `${cn}|||${sn}`
  // A slot marked "not taught" in Teacher Allocation is stored under the grid's own key,
  // `Branch|||Curriculum|||Class|||Subject` (branch "Main" when the class has none); older builds
  // wrote `Class|||Subject`. Both are honoured so such a slot never reaches the server.
  const isDeletedSlot = (
    className: string,
    subjectName: string,
    branch?: string,
    curriculum?: string
  ) => {
    if (deletedSlots.has(slotKey(className, subjectName))) return true
    const cls = resolveClass(className, branch, curriculum)
    const slotBranch = String(cls?.branch ?? branch ?? '').trim() || 'Main'
    const slotCurriculum = String(cls?.curriculum ?? curriculum ?? '').trim()
    return deletedSlots.has(`${slotBranch}|||${slotCurriculum}|||${className}|||${subjectName}`)
  }
  const subjectEntryKey = (e: {
    name: string
    class: string
    branch: string
    level: string
    curriculum?: string
  }) => `${e.name}|||${e.class}|||${e.branch}|||${e.level}|||${curriculumKey(e.curriculum)}`

  // The subjects section is the UNION of two sources, keyed per (subject, class, branch, level):
  //  1. the curriculum selections (manualData.subjects) - every pair the wizard knows, with
  //     whatever teacher/frequency the row already carries (usually none before Lesson Planning);
  //  2. the lesson-planning grid - the same pairs once a teacher, frequency and duration are set.
  // A lesson-planning entry wins for its pair. The grid used to REPLACE the selections outright
  // as soon as any cell had a teacher, which dropped every pair still without one: a class,
  // subject or level added after the grid was first filled never reached the server, and the
  // students sync that rides in the same request then failed on it with
  // "Not linked - subject X does not exist in class Y".
  const mergedSubjects = new Map<string, SubjectEntry>()

  ;(manualData.subjects ?? [])
    .flatMap((subject): SubjectEntry[] => {
      const name = String(subject?.name ?? '').trim()
      if (!name) return []
      const className = String(subject?.class ?? '').trim()
      const explicitClass = className
        ? resolveClass(className, subject?.branch, subject?.curriculum)
        : undefined
      const baseBranch = String(
        subject?.branch || explicitClass?.branch || primaryBranch || ''
      ).trim()
      const baseLevel = String(subject?.level || explicitClass?.level || primaryLevel || '').trim()
      const baseCurriculum = levelCurricula.resolve(
        baseBranch,
        baseLevel,
        subject?.curriculum || explicitClass?.curriculum
      )
      if (!className) {
        // Level-wide row (legacy shape): one entry per class of that level.
        const candidateClasses =
          classesByBranchLevel.get(
            `${baseBranch}|||${curriculumKey(baseCurriculum)}|||${baseLevel}`
          ) ?? []
        return candidateClasses.map((cls: any) => {
          const clsName = String(cls?.name ?? '').trim()
          const persistedId = lookupSubjectId(
            subjectIdByNameAndClass,
            name,
            clsName,
            cls?.curriculum,
            cls?.branch
          )
          return {
            name,
            teacher: String(subject?.teacher ?? '').trim(),
            class: clsName,
            frequency: String(subject?.frequency ?? '').trim() || defaultFrequencyStr,
            duration: String(subject?.duration ?? '').trim() || defaultDurationStr,
            branch: String(cls?.branch ?? '').trim() || baseBranch,
            level: String(cls?.level ?? '').trim() || baseLevel,
            curriculum: cls?.curriculum || baseCurriculum,
            action: persistedId ? 'update' : inferAction(subject as any, 'insert'),
            id: persistedId || null,
          }
        })
      }
      return [
        {
          name,
          teacher: String(subject?.teacher ?? '').trim(),
          class: className,
          frequency: String(subject?.frequency ?? '').trim() || defaultFrequencyStr,
          duration: String(subject?.duration ?? '').trim() || defaultDurationStr,
          branch: baseBranch,
          level: baseLevel,
          curriculum: baseCurriculum,
          action: inferAction(subject as any, 'insert'),
          id: parsePositiveNumber((subject as any)?.id) || null,
        },
      ]
    })
    // A slot the user removed in the lesson-planning grid stays removed.
    .filter(
      (entry) =>
        entry.class && !isDeletedSlot(entry.class, entry.name, entry.branch, entry.curriculum)
    )
    .forEach((entry) => {
      const key = subjectEntryKey(entry)
      if (!mergedSubjects.has(key)) mergedSubjects.set(key, entry)
    })

  Object.entries(lessonPlanData).forEach(([classKey, subjectMap]) => {
    // classKey format: 'BranchName|||Curriculum|||ClassName' or legacy 'BranchName|||ClassName'
    const parts = classKey.split('|||')
    const actualClassName = parts.length >= 3 ? parts[2] : parts.length === 2 ? parts[1] : classKey
    // The key's own branch segment is authoritative; the class row confirms the level.
    const keyBranch = parts.length >= 2 ? String(parts[0] ?? '').trim() : ''
    // The key's curriculum segment picks between same-named classes of two curricula.
    const keyCurriculum = parts.length >= 3 ? String(parts[1] ?? '').trim() : ''
    const relatedClass = resolveClass(actualClassName, keyBranch, keyCurriculum)
    if (!relatedClass) return
    const branch = keyBranch || String(relatedClass.branch ?? '').trim() || primaryBranch
    const level = String(relatedClass.level ?? '').trim() || primaryLevel
    const curriculum = levelCurricula.resolve(
      branch,
      level,
      relatedClass.curriculum || keyCurriculum
    )
    Object.entries(subjectMap ?? {}).forEach(([subjectName, cell]) => {
      if (!cell?.teacher?.trim()) return
      if (
        deletedSlots.has(slotKey(classKey, subjectName)) ||
        deletedSlots.has(slotKey(actualClassName, subjectName))
      ) {
        return
      }
      // Ids are per class: the persisted (name, class) id, else the id on the matching
      // per-class curriculum row. Never fall back to a same-named subject of ANOTHER class -
      // the server updates by id and ignores the class in the row, so that rewrote the other
      // class's subject and left this class (typically one just added) without one.
      const existingId =
        lookupSubjectId(
          subjectIdByNameAndClass,
          subjectName,
          actualClassName,
          curriculum,
          branch
        ) ||
        parsePositiveNumber(
          subjectByNameAndClass.get(subjectRowKey(subjectName, actualClassName, curriculum, branch))
            ?.id
        )
      const entry: SubjectEntry = {
        name: subjectName,
        teacher: resolveTeacherApiName(cell.teacher, (cell as any).teacherEmployeeId),
        class: actualClassName,
        frequency: (cell.frequency ?? '').trim() || defaultFrequencyStr,
        duration: (cell.duration ?? '').trim() || defaultDurationStr,
        branch,
        level,
        curriculum,
        action: existingId ? 'update' : 'insert',
        id: existingId || null,
      }
      mergedSubjects.set(subjectEntryKey(entry), entry)
    })
  })

  const subjects: any[] = Array.from(mergedSubjects.values()).map((entry) => {
    const payload: any = {
      name: entry.name,
      teacher: entry.teacher,
      class: entry.class,
      frequency: entry.frequency,
      duration: entry.duration,
      branch: entry.branch,
      level: entry.level,
      ...curriculumFields(entry.curriculum),
      action: entry.action,
    }
    addIdIfPresent(payload, entry)
    const roomId = parsePositiveNumber(venueIdByClassName[entry.class])
    if (roomId) payload.roomId = roomId
    return payload
  })

  // Subjects removed from a level: their saved rows go as deletes (by id; the class, branch and
  // level let the server check the id belongs to that class). Not for a class or branch being
  // deleted - the subject goes with it - nor for one that is back in the list.
  const liveSubjectKeys = new Set(
    subjects.map((entry) =>
      [entry.name, entry.class, entry.branch]
        .map((v) => String(v ?? '').trim().toLowerCase())
        .join('|||')
    )
  )
  const deletedSubjectPayloads = (manualData.deletedSubjects ?? []).flatMap((entry) => {
    const id = parsePositiveNumber(entry?.id)
    const className = String(entry?.class ?? '').trim()
    if (!id || !className || inDeletedBranch(entry?.branch)) return []
    const branchName = String(entry?.branch ?? '').trim() || primaryBranch
    const key = [entry.name, className, branchName]
      .map((v) => String(v ?? '').trim().toLowerCase())
      .join('|||')
    if (liveSubjectKeys.has(key)) return []
    const classRow = {
      name: className,
      branch: branchName,
      level: entry?.level,
      curriculum: entry?.curriculum,
    }
    if (deletedClassSeen.has(classIdentityKey(classRow))) return []
    return [
      {
        id,
        name: String(entry?.name ?? '').trim(),
        class: className,
        branch: branchName,
        level: String(entry?.level ?? '').trim() || primaryLevel,
        ...curriculumFields(entry?.curriculum),
        action: 'delete',
      },
    ]
  })

  const students = (manualData.students ?? []).map((student: any) => {
    const nameParts = splitFullName(student?.full_name ?? '')
    const first_name = student?.first_name ?? nameParts.first_name
    const last_name = student?.last_name ?? nameParts.last_name
    // Placement by branch AND level: the same class name can exist under two levels
    // ("Cambridge Stage 7-A" under Primary and Lower Secondary). The row's own branch/level
    // (stored by the upload from the template's qualified label) win; otherwise the class's,
    // but only when the name pins exactly one class. An ambiguous name is sent with NO level
    // so the server refuses it with its own message — resolveClass (branch + name, first
    // match wins) and the primaryLevel guess put such rows in the wrong level.
    const placement = resolveStudentPlacement(manualData.classes ?? [], student, primaryBranch)
    const studentBranch = placement.branch
    const studentLevel = placement.level
    const studentCurriculum = levelCurricula.resolve(
      studentBranch,
      studentLevel,
      placement.curriculum
    )
    const normalizedSubjects = (student?.subjects ?? []).map((rawSubject: any) => {
      // File-imported subjects are stored as encoded "name|||class" strings.
      const subject =
        typeof rawSubject === 'string'
          ? {
              name: String(rawSubject.split('|||')[0] ?? '').trim(),
              class: String(rawSubject.split('|||')[1] ?? '').trim(),
            }
          : rawSubject
      const subjectName = subject?.name ?? ''
      const subjectClass = subject?.class || student?.class || ''
      // Same rules as the student: the subject's own hints, else the student's.
      const subjectPlacement = resolveStudentPlacement(
        manualData.classes ?? [],
        {
          class: subjectClass,
          branch: subject?.branch || studentBranch,
          level: subject?.level || studentLevel,
          // A subject of another level carries that level's curriculum, not the student's.
          curriculum: subject?.curriculum || (subject?.level ? undefined : placement.curriculum),
        },
        studentBranch
      )
      return {
        name: subjectName,
        class: subjectClass,
        branch: subjectPlacement.branch,
        ...(subjectPlacement.level ? {level: subjectPlacement.level} : {}),
        ...curriculumFields(
          subjectPlacement.level
            ? levelCurricula.resolve(
                subjectPlacement.branch,
                subjectPlacement.level,
                subjectPlacement.curriculum
              )
            : undefined
        ),
      }
    })

    return {
      first_name,
      last_name,
      email: student?.email ?? '',
      cnic: student?.cnic ?? '',
      class: student?.class ?? '',
      // The class the user picked (or the upload placed), by id: the backend takes it when it
      // is an onboarding class with this same name, so a name two branches share is placed
      // exactly. Another name, and the row is placed by name as before.
      ...(parsePositiveNumber(student?.classId) ? {classId: Number(student.classId)} : {}),
      // The backend places the student in the class named by class + branch + level; a class
      // name alone that exists in two branches or levels is refused. `level` is omitted when
      // the portal could not pin one, on purpose; `curriculum` likewise.
      branch: studentBranch,
      ...(studentLevel ? {level: studentLevel} : {}),
      ...curriculumFields(studentLevel ? studentCurriculum : undefined),
      registrationNumber: student?.registrationNumber ?? '',
      subjects: normalizedSubjects.map((subject: any) => {
        const subjectPayload: any = {
          ...subject,
          action: inferAction(subject, 'insert'),
        }
        addIdIfPresent(subjectPayload, subject)
        return subjectPayload
      }),
      action: inferAction(student, 'insert'),
    }
  })

  students.forEach((student: any, index: number) => {
    addIdIfPresent(student, (manualData.students ?? [])[index])
  })

  // Removed saved students: `action:'delete'`, found by id or registration number. One the
  // user added back (same registration number, or same name when it has none) is not deleted.
  const presentStudentKeys = new Set((manualData.students ?? []).map(studentRowKey))
  const deletedStudentSeen = new Set<string>()
  const deletedStudentPayloads = (manualData.deletedStudents ?? [])
    .filter((student) => {
      const key = studentRowKey(student)
      if (!parsePositiveNumber(student?.id) && !String(student?.registrationNumber ?? '').trim()) {
        return false
      }
      if (presentStudentKeys.has(key) || deletedStudentSeen.has(key)) return false
      deletedStudentSeen.add(key)
      return true
    })
    .map((student) => {
      const {first_name, last_name} = splitFullName(student.full_name ?? '')
      const payload: any = {
        first_name,
        last_name,
        email: '',
        cnic: '',
        class: student.class ?? '',
        branch: student.branch || primaryBranch,
        ...(student.level ? {level: student.level} : {}),
        registrationNumber: String(student.registrationNumber ?? '').trim(),
        subjects: [],
        action: 'delete',
      }
      addIdIfPresent(payload, student)
      return payload
    })

  const finalVenues = (() => {
    if (manualData.venuesSameForClass) {
      const uniqueClassNames = Array.from(
        new Set(classes.map((cls: any) => String(cls?.name ?? '').trim()).filter(Boolean))
      )
      return uniqueClassNames.map((name, i) => ({name: `Venue ${i + 1} (${name})`}))
    }
    const existing = manualData.venues ?? []
    // Check localStorage for auto-generated venue count, fallback to state
    const storedAutoCount =
      typeof window !== 'undefined'
        ? parseInt(localStorage.getItem('onboarding_venues_auto_count') || '0', 10)
        : 0
    const autoCount = storedAutoCount || (manualData.venuesAutoCount ?? 0)
    if (existing.length === 0 && autoCount > 0) {
      return Array.from({length: autoCount}, (_, i) => ({name: `Venue ${i + 1}`}))
    }
    return existing
  })()
  const venues = finalVenues.map((venue: any) => {
    const payload: any = {
      name: venue?.name ?? '',
      action: inferAction(venue, 'insert'),
    }
    addIdIfPresent(payload, venue)
    return payload
  })

  return {
    branch,
    duration: {
      period: manualData.durationName ?? '',
      start_date: manualData.durationStartDate ?? '',
      end_date: manualData.durationEndDate ?? '',
    },
    timings,
    break_timings,
    teachers,
    classes: [...(classes as any[]), ...deletedClassPayloads],
    subjects: [...subjects, ...deletedSubjectPayloads],
    students: [...students, ...deletedStudentPayloads],
    venues,
  }
}

const parsePositiveNumber = (value: unknown): number | null => {
  const parsed = Number(value)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null
}

const normalizeOption = (
  item: any,
  idCandidates: string[],
  labelCandidates: string[]
): TimetableOption | null => {
  if (!item) return null
  const id = idCandidates.map((k) => parsePositiveNumber(item?.[k])).find(Boolean)
  if (!id) return null
  const label = labelCandidates
    .map((k) => item?.[k])
    .find((v) => typeof v === 'string' && v.trim().length > 0)
  const option: TimetableOption = {
    id,
    name: (label as string) || `#${id}`,
  }
  const branchId = parsePositiveNumber(item?.branchId ?? item?.branch_id)
  const levelId = parsePositiveNumber(item?.levelId ?? item?.level_id)
  if (branchId) option.branchId = branchId
  if (levelId) option.levelId = levelId
  if (typeof item?.start_date === 'string') option.startDate = item.start_date
  if (typeof item?.end_date === 'string') option.endDate = item.end_date
  if (typeof item?.startDate === 'string') option.startDate = item.startDate
  if (typeof item?.endDate === 'string') option.endDate = item.endDate
  return option
}

const uniqueById = (items: TimetableOption[]) => {
  const map = new Map<number, TimetableOption>()
  items.forEach((item) => {
    if (!map.has(item.id)) {
      map.set(item.id, item)
    }
  })
  return Array.from(map.values())
}

const extractTimetableSelections = (response: any) => {
  const source = response?.result ?? response?.data ?? response ?? {}
  const created = source?.created ?? source?.result?.created ?? {}
  const updated = source?.updated ?? source?.result?.updated ?? {}

  const mergeBucketArrays = (legacy: any, createdItems: any, updatedItems: any) => {
    const legacyArray = Array.isArray(legacy) ? legacy : legacy ? [legacy] : []
    const createdArray = Array.isArray(createdItems)
      ? createdItems
      : createdItems
        ? [createdItems]
        : []
    const updatedArray = Array.isArray(updatedItems)
      ? updatedItems
      : updatedItems
        ? [updatedItems]
        : []
    return [...legacyArray, ...createdArray, ...updatedArray]
  }

  const branchRaw =
    mergeBucketArrays(source?.branches, created?.branches, updated?.branches) ??
    source?.branchOptions ??
    source?.available_branches ??
    source?.branch_list ??
    []
  const levelRaw =
    mergeBucketArrays(source?.levels, created?.levels, updated?.levels) ??
    source?.levelOptions ??
    source?.available_levels ??
    source?.level_list ??
    []
  const durationRaw =
    mergeBucketArrays(source?.durations, created?.durations, updated?.durations) ??
    source?.durationOptions ??
    source?.available_durations ??
    source?.duration_list ??
    []

  const branchOptions = uniqueById(
    (Array.isArray(branchRaw) ? branchRaw : [branchRaw])
      .map((item) =>
        normalizeOption(item, ['id', 'branchId', 'branch_id'], ['name', 'branch_name'])
      )
      .filter(Boolean) as TimetableOption[]
  )
  const levelOptions = uniqueById(
    (Array.isArray(levelRaw) ? levelRaw : [levelRaw])
      .map((item) => normalizeOption(item, ['id', 'levelId', 'level_id'], ['name', 'level_name']))
      .filter(Boolean) as TimetableOption[]
  )
  const durationOptions = uniqueById(
    (Array.isArray(durationRaw) ? durationRaw : [durationRaw])
      .map((item) =>
        normalizeOption(
          item,
          ['id', 'durationId', 'duration_id'],
          ['name', 'period', 'duration_name']
        )
      )
      .filter(Boolean) as TimetableOption[]
  )

  const selectedBranchId =
    parsePositiveNumber(source?.selectedBranchId ?? source?.branchId ?? source?.branch_id) ??
    branchOptions[0]?.id ??
    null
  const selectedLevelId =
    parsePositiveNumber(source?.selectedLevelId ?? source?.levelId ?? source?.level_id) ??
    levelOptions[0]?.id ??
    null
  const selectedDurationId =
    parsePositiveNumber(source?.selectedDurationId ?? source?.durationId ?? source?.duration_id) ??
    durationOptions[0]?.id ??
    null

  return {
    branchOptions,
    levelOptions,
    durationOptions,
    selectedBranchId,
    selectedLevelId,
    selectedDurationId,
  }
}

const ONBOARDING_WELCOME_SEEN_KEY = 'onboarding_welcome_seen'
const ONBOARDING_SAMPLE_DATA_UNAVAILABLE_KEY = 'onboarding_sample_data_unavailable'
/** Quiet time before the setup bucket (students, timings…) is written to preferences. */
const SETUP_SAVE_DEBOUNCE_MS = 1000
/** The teachers section of a school the wizard has sent no teachers for yet. */
const EMPTY_TEACHERS_SNAPSHOT = getSectionSnapshot('teachers', {} as ManualData)

const buildSampleDataTimetablePath = (
  branchId: number,
  levelId: number,
  durationId: number
): string =>
  // The Master Timetable Generation page with the sample selection; the one-time welcome popup
  // (SampleCreditsInfoModal) opens over it.
  `/institute/schedule/timetable/new-version/${branchId}/${levelId}/${durationId}`

const pickFirstBranchId = (branches: unknown): number | null => {
  if (!Array.isArray(branches) || branches.length === 0) return null
  const sorted = [...branches].sort(
    (a: {id?: number}, b: {id?: number}) => (b?.id ?? 0) - (a?.id ?? 0)
  )
  const id = sorted[0]?.id
  return typeof id === 'number' && id > 0 ? id : null
}

/** Restored wizard state for a tenant, or null when there is none (or no tenant id yet). */
const readStoredWizardState = (
  storageKey: string | null
): {currentStep?: number; data?: WizardData} | null => {
  if (!storageKey) return null
  const saved = localStorage.getItem(storageKey)
  if (!saved) return null
  try {
    return JSON.parse(saved)
  } catch {
    return null
  }
}

const OnboardingWizard: FC = () => {
  const intl = useIntl()
  const dispatch = useDispatch()
  const urddId = useSelector<RootState>((state) => state.userReducer.urddId) as string | null
  // A support/impersonated session (support_user_id claim on the token) may VIEW any wizard
  // step, but must never WRITE wizard state — not the preferences progress pointer, not
  // onboarding analytics events. Merely rendering a deep-linked step ranks as forward
  // progress to the save effect below, so an impersonating admin would otherwise overwrite
  // the tenant's real position (and node-api-subscription would backfill every intermediate
  // sub-step as if genuinely completed). Same exclusion node-api's portalLoginSignal applies
  // to the E16-E18 idle signals.
  const {isSupportSession} = useSupportUser()
  const generalSettings = useSelector<RootState>(
    ({mainReducer}) => mainReducer.generalSettings
  ) as any
  const mainContentRef = useRef<HTMLDivElement | null>(null)
  const [isGenerating, setIsGenerating] = useState(false)
  const [isSeedingSampleData, setIsSeedingSampleData] = useState(false)
  const [isAutofillLoading, setIsAutofillLoading] = useState(false)
  // True from mount until the server fetches (preferences + /onboarding/data) settle. Used to show
  // skeletons on the data-heavy resume screens (classes/review) instead of a flash of empty state.
  const [isHydrating, setIsHydrating] = useState(true)
  // Set by the resume effect for brand-new users (no saved server progress): the welcome
  // screen that used to ask "sample data or your own data?" is gone, so every new school is
  // seeded with sample data automatically and taken to the dashboard. Consumed by the effect
  // below `handleStartWithSampleData`, which is declared later in the component.
  const [autoSeedRequested, setAutoSeedRequested] = useState(false)
  // "Sample data cleared" confirmation: shown once when the tenant lands here right after clearing
  // its sample workspace (see decideNewUserEntry). Continuing from it starts the real setup.
  const [showSampleClearedScreen, setShowSampleClearedScreen] = useState(false)
  // Wizard state is cached per tenant. The key used to be browser-global, so onboarding a second
  // school in the same browser restored the first one's answers at mount — which pre-filled the
  // wrong data AND made the preference-writing effects below fire before the new tenant had seen
  // a screen. Null when there is no tenant id yet: we then neither restore nor persist rather
  // than fall back to a shared key.
  const storageKey = useMemo(() => onboardingWizardStateKey(urddId), [urddId])
  // Read once, lazily: both state initializers below share it. A support session ignores
  // the cache entirely: the operator's browser may hold a stale copy from a PREVIOUS
  // impersonation of this tenant, and diagnosis should mirror the server state alone.
  const [restoredState] = useState(() =>
    isSupportSession ? null : readStoredWizardState(storageKey)
  )

  // Clamp positions saved under the old 6-step numbering (before the Data
  // Import step was removed); the URL/prefs resume settles the exact step.
  const [currentStep, setCurrentStep] = useState<number>(() =>
    Math.min(restoredState?.currentStep || 1, 5)
  )

  const prevStepRef = useRef<number>(currentStep)
  useEffect(() => {
    const prev = prevStepRef.current
    if (prev === 4 && currentStep === 2) {
      setData((p) => ({
        ...p,
        manualData: {
          ...p.manualData,
          manualActiveTab: 'teachers',
        },
      }))
    }
    prevStepRef.current = currentStep
  }, [currentStep])

  const [data, setData] = useState<WizardData>(() =>
    // Sessions saved before the Data Import screen was removed may carry
    // importType null/'csv'; the wizard only has the manual flow now.
    restoredState?.data ? {...restoredState.data, importType: 'manual'} : initialData
  )

  // --- "Did the user do this, or did we?" -------------------------------------------------
  // The wizard mirrors its state into user preferences (setup/curriculum/classesDraft/mode) for
  // resume. node-api also infers the onboarding funnel from those buckets — `reached` from the
  // progress pointer, `saved` from the data buckets — and writes each (tenant, sub-step) row
  // exactly once, deduplicated forever. First write wins permanently, so anything that lands in
  // the blob before the user acted makes that sub-step uncountable for that tenant for good.
  //
  // Two guards keep restored/pre-filled values out of it:
  //  - `hydratingRef`: true from mount until the first genuine user edit. Restoring state (from
  //    localStorage, from preferences, from GET /onboarding/data) is hydration, not change, so
  //    the preference-writing effects stay silent until the user actually touches something.
  //  - `touchedSetupFieldsRef`: the setup fields the user has edited. `extractOnboardingSetup`
  //    serialises only these, so a screen default the wizard pre-fills for the user (the "Main
  //    Branch" row, the default subject frequency) never reads as "the user saved this screen".
  //    Seeded on resume with whatever preferences already hold, so a later edit to one field
  //    doesn't rewrite the bucket without the others.
  const hydratingRef = useRef(true)
  const touchedSetupFieldsRef = useRef<Set<string>>(new Set())

  /**
   * The analytics positions the user has actually EDITED: a value they changed, as opposed to
   * a prefill they accepted. Read by `editedIn` when a main step's Next click completes it.
   *
   * SEPARATE FROM touchedSetupFieldsRef ON PURPOSE. That ref is force-populated by
   * setupFieldsPassedBefore() when the user advances, because advancing IS completion of the
   * screen. This one must never be: "completed" and "edited" are two different facts, and the
   * backend already has the first. It also covers every step, not just the five Core Setup
   * fields. Populated only from user writes (never `prefill: true`), only by value, and only
   * for the main step the user is on (see editedPositionsIn).
   */
  const editedPositionsRef = useRef<Set<string>>(new Set())
  /**
   * The country Setup Basics pre-fills, as Step1Mode resolved it; `undefined` until it has.
   * The `mode` position is judged by value against it on Next (modeSelectionEdited), not via
   * editedPositionsRef, which a reload between the pick and Next would empty.
   */
  const defaultCountryRef = useRef<string | null | undefined>(undefined)
  const handleDefaultCountryResolved = useCallback((country: string | null) => {
    defaultCountryRef.current = country
  }, [])
  // `updateData` is memoised with no deps, so it reads the live step through a ref.
  const currentStepRef = useRef(currentStep)
  currentStepRef.current = currentStep

  const sentSectionSnapshotsRef = useRef<Partial<Record<IncrementalSectionKey, string>>>({})
  /**
   * A `bulk-import` is on its way. The "already sent" snapshots are only taken once it answers,
   * so a second save started meanwhile (a double click on Next, two step callbacks) would post
   * the same sections again; it is ignored instead. Set before the dispatch, cleared in both of
   * its callbacks.
   */
  const syncInFlightRef = useRef(false)

  const getSectionsForFields = (fields: string[]) => {
    const sections = new Set<IncrementalSectionKey>()
    fields.forEach((checkpoint) => {
      ;(CHECKPOINT_SECTIONS[checkpoint] ?? []).forEach((section) => sections.add(section))
      if (checkpoint === 'subjects') {
        sections.add('subjects')
      }
    })
    return Array.from(sections)
  }
  useEffect(() => {
    // Support sessions must not overwrite the tenant-keyed cache in the operator's browser
    // either — a later REAL login there (or the next impersonation) would restore it.
    if (!storageKey || isSupportSession) return
    localStorage.setItem(storageKey, JSON.stringify({currentStep, data}))
  }, [storageKey, currentStep, data, isSupportSession])

  // --- Per-section URL routing (/onboarding/<slug>) ---------------------------------
  // The :section param lets the backend deep-link a user straight to a specific
  // onboarding section. We resolve the slug -> wizard step + initial sub-section, and
  // (at step boundaries) write the URL back so it stays meaningful as the user advances.
  const navigate = useNavigate()
  const location = useLocation()
  const {step: stepParam, subStep: subStepParam} = useParams<{step?: string; subStep?: string}>()

  // Initial sub-position passed into the active step component (read on mount).
  const [initialCoreSetupSubStep, setInitialCoreSetupSubStep] = useState<number | undefined>(
    undefined
  )
  const [initialManualTab, setInitialManualTab] = useState<
    'teachers' | 'students' | 'lessonPlanning' | undefined
  >(undefined)
  // Active sub-step slug reported by the current step component; drives the URL.
  /** Which screen of the Teachers tab a /assign-subjects/<sub-step> URL opens (entry vs review). */
  const [initialTeachersPhase, setInitialTeachersPhase] = useState<'entry' | 'review' | undefined>(
    undefined
  )
  const [activeSubStepSlug, setActiveSubStepSlug] = useState<string | null>(null)

  // Left stepper sidebar collapses to an icon rail (like the app sidebar) for more room.
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const isLessonPlanningScreen = currentStep === 4 && activeSubStepSlug === 'lesson-planning'
  // Screens that need the width: the legacy Lesson Planning grid. Auto-collapse on entry and
  // restore the user's own choice on the way out. null = not currently overriding. The Teacher
  // Allocation V2 screens are laid out for the open sidebar, so it is left as the user set it.
  const isWideScreen = isLessonPlanningScreen && !TEACHER_ALLOCATION_V2
  const collapsedBeforeWideScreenRef = useRef<boolean | null>(null)
  useEffect(() => {
    if (isWideScreen) {
      setSidebarCollapsed((previous) => {
        collapsedBeforeWideScreenRef.current = previous
        return true
      })
    } else if (collapsedBeforeWideScreenRef.current !== null) {
      setSidebarCollapsed(collapsedBeforeWideScreenRef.current)
      collapsedBeforeWideScreenRef.current = null
    }
  }, [isWideScreen])

  const writeBackReadyRef = useRef(false)

  // --- Server-side resume (user/preferences) ---------------------------------------
  // Progress is mirrored into `OnboardingWizard.progress` in the user's preferences so a
  // user who skips/leaves and logs back in (even on a fresh device) resumes where they
  // left off. `preferencesRef` caches the full preferences object so incremental saves
  // merge without clobbering unrelated keys; `furthestRankRef` enforces forward-only
  // saves (editing an earlier sub-step never drags the saved resume point backward).
  const preferencesRef = useRef<Record<string, any> | null>(null)
  // "Save and finish later" (Teacher Allocation handover, Point 5).
  const [saveLaterStatus, setSaveLaterStatus] = useState<SaveLaterStatus>('idle')
  const [saveLaterModalOpen, setSaveLaterModalOpen] = useState(false)
  const [setupPaused, setSetupPaused] = useState(false)
  const step4FlushRef = useRef<(() => WizardData['manualData']) | null>(null)
  /** Academic Structure's "Save and finish later": the structure on screen, folded as Next does. */
  const step3FlushRef = useRef<(() => WizardData['manualData']) | null>(null)
  const savedAtRef = useRef(0)
  const furthestRankRef = useRef<number>(-1)
  const [prefsLoaded, setPrefsLoaded] = useState(false)
  const resumeStartedRef = useRef(false)
  const onboardingDataFetchedRef = useRef(false)

  // --- Server-side data prefill coordination ----------------------------------------------
  // Prefilling completed steps needs BOTH the saved curriculum config (from preferences) and the
  // saved entities (from GET /onboarding/data). These resolve independently, so each callback
  // stashes its result and calls `tryHydrateFromServer`, which runs the mapper once both are ready.
  const prefsDoneRef = useRef(false)
  const onboardingDataDoneRef = useRef(false)
  const onboardingDataResultRef = useRef<any>(null)
  const onboardingCurriculumConfigRef = useRef<OnboardingCurriculumConfig | null>(null)
  const onboardingSetupRef = useRef<OnboardingSetup | null>(null)
  const onboardingClassesDraftRef = useRef<OnboardingClassesDraft | null>(null)
  /** OnboardingWizard.teachersDraft and teachersConfirmedAt as preferences held them on mount. */
  const onboardingTeachersDraftRef = useRef<unknown>(null)
  const onboardingTeachersConfirmedAtRef = useRef<unknown>(null)
  const hydratedFromServerRef = useRef(false)
  // Set while re-hydrating after the Lesson Planning Excel import: the imported data is
  // already server-side, so every section snapshot is seeded as "sent" and the next
  // Next-press has nothing to bulk-import unless the user edits something afterwards.
  const seedAllSectionsRef = useRef(false)
  const lastSavedCurriculumRef = useRef<string | null>(null)
  const lastSavedSetupRef = useRef<string | null>(null)
  const lastSavedClassesDraftRef = useRef<string | null>(null)
  /** Teachers section snapshot of the last teachers draft written (or restored). */
  const lastSavedTeachersDraftRef = useRef<string | null>(null)

  const tryHydrateFromServer = useCallback(() => {
    if (hydratedFromServerRef.current) return
    // Wait until both server fetches have settled: preferences (curriculum config + the non-API
    // setup sections) and GET /onboarding/data (branches/levels/classes/subjects/teachers).
    if (!prefsDoneRef.current || !onboardingDataDoneRef.current) return
    hydratedFromServerRef.current = true

    const result = onboardingDataResultRef.current
    // FE-1/FE-3: plain level names from the prefill only once resume-data proves this school's
    // backend stores the curriculum per level. Set before the steps are released below so
    // their first prefill call already asks for the right form.
    setPlainCurriculumNames(shouldRequestPlainNames(detectLevelCurriculumSupport(result)))
    setIsHydrating(false)
    const setup: OnboardingSetup = onboardingSetupRef.current ?? ({} as OnboardingSetup)
    setData((prev) => {
      // Apply the saved setup (incl. persisted branches) onto the base first so the mapper's
      // branch fallback can pick them up when /onboarding/data returns no campuses.
      const baseManualData = {...prev.manualData, ...setup}
      // From /onboarding/data (may be empty if the call failed or returned nothing).
      const mapped = result
        ? mapOnboardingDataToManualData(
            result,
            onboardingCurriculumConfigRef.current,
            baseManualData
          )
        : {}
      const classesDraft = onboardingClassesDraftRef.current
      // Not after an Excel import: the server was just written, the mount-time draft is older.
      const teachersDraft = seedAllSectionsRef.current
        ? null
        : teachersDraftToRestore(
            onboardingTeachersDraftRef.current,
            onboardingTeachersConfirmedAtRef.current
          )
      const hasSetup = setup && Object.keys(setup).length > 0
      const hasMapped = mapped && Object.keys(mapped).length > 0
      const hasClassesDraft = classesDraft && Object.keys(classesDraft).length > 0
      if (!hasSetup && !hasMapped && !hasClassesDraft && !teachersDraft) return prev
      // Setup fills the sections /onboarding/data doesn't return; mapped (API) wins for the rest.
      const mergedManual = {...baseManualData, ...mapped}
      // Curriculum codes live only in preferences (GET /onboarding/data knows no curriculum);
      // without them a resumed tenant would fall back to the legacy details endpoint.
      const savedCodes = onboardingCurriculumConfigRef.current?.curriculumCodes
      if (savedCodes && Object.keys(savedCodes).length > 0) {
        mergedManual.curriculumCodes = {...savedCodes, ...(mergedManual.curriculumCodes ?? {})}
      }
      const savedShortCodes = onboardingCurriculumConfigRef.current?.curriculumShortCodes
      if (savedShortCodes && Object.keys(savedShortCodes).length > 0) {
        mergedManual.curriculumShortCodes = {
          ...savedShortCodes,
          ...(mergedManual.curriculumShortCodes ?? {}),
        }
      }
      const savedPrefillKey = onboardingCurriculumConfigRef.current?.institutionPrefillKey
      if (savedPrefillKey && !mergedManual.institutionPrefillKey) {
        mergedManual.institutionPrefillKey = savedPrefillKey
      }
      // Custom curriculum names likewise live only in preferences.
      const savedCustom = onboardingCurriculumConfigRef.current?.customCurriculums
      if (savedCustom && savedCustom.length > 0 && !(mergedManual.customCurriculums ?? []).length) {
        mergedManual.customCurriculums = [...savedCustom]
      }
      // Branches are a special case. The bulk-import API only persists branches at the CURRICULUM
      // step, so a user who edits branches in core-setup and leaves before curriculum has the real
      // list in preferences but NOT server-side — /onboarding/data still returns just the default
      // branch, which would otherwise clobber (or re-add a deleted) branch. The `setup` prefs
      // bucket is saved on every edit (adds AND removals), so it is always the fresher source of
      // truth: when it has branches, it fully wins over the (stale) API branches. Legacy users
      // with no persisted branches fall back to the API list.
      // Names only: a pointer saved by an older build may hold `{name, id}` entries.
      const prefBranches = Array.isArray(setup.branches)
        ? (setup.branches as unknown[])
            .map((entry: any) =>
              String(typeof entry === 'string' ? entry : (entry?.name ?? '')).trim()
            )
            .filter(Boolean)
        : []
      if (prefBranches.length > 0) {
        mergedManual.branches = prefBranches
      }
      // Classes-draft fallback: classes are only persisted to the backend after the Review
      // sub-step, so a user who filled classes and left at the Sections sub-step has none in the
      // API response. In THAT case only (backend returned no classes) restore them from the draft.
      const backendClasses = (mapped as Partial<typeof baseManualData>).classes
      const backendHasClasses = Array.isArray(backendClasses) && backendClasses.length > 0
      // When the backend DID return classes it wins — except for subjects, which it only learns
      // on the Lesson Planning sync: until then its classes come back subject-less and the
      // draft is the only holder of what the user picked (see fillSubjectsFromDraft).
      const finalManual =
        hasClassesDraft && !backendHasClasses
          ? mergeClassesDraft(mergedManual, classesDraft)
          : hasClassesDraft
            ? fillSubjectsFromDraft(mergedManual, classesDraft)
            : mergedManual
      // Sections round-tripped from the API are already in sync server-side — seed their sent
      // snapshots so resume doesn't re-send them untouched. Without this, leaving e.g. the teachers
      // tab after a resume re-submits every hydrated teacher as an update even when nothing
      // changed (first-time flow only ever syncs what the user actually entered). Only API-sourced
      // sections are seeded; prefs-only data (branches/duration/venues/draft classes) still syncs.
      const mappedAny = mapped as Record<string, any>
      const apiSyncedSections: IncrementalSectionKey[] = seedAllSectionsRef.current
        ? // Post-Excel-import refresh: everything just came from the server, so treat every
          // section as already synced — Next should not re-send it via bulk-import. Except
          // students: GET onboarding/data does not return them, so ones added in this visit
          // (a file import, say) are still unsent and keep their last-sent snapshot.
          [
            'branch',
            'duration',
            'venues',
            'timings',
            'teachers',
            'classes',
            'subjects',
            'lessonPlanning',
          ]
        : []
      seedAllSectionsRef.current = false
      const backendHasTeachers = Array.isArray(mappedAny.teachers) && mappedAny.teachers.length > 0
      if (apiSyncedSections.length === 0) {
        // With a teachers draft the snapshot is seeded below, from the server's list.
        if (backendHasTeachers && !teachersDraft) apiSyncedSections.push('teachers')
        if (backendHasClasses) apiSyncedSections.push('classes')
        if (Array.isArray(mappedAny.subjects) && mappedAny.subjects.length > 0) {
          apiSyncedSections.push('subjects')
        }
      }
      apiSyncedSections.forEach((section) => {
        sentSectionSnapshotsRef.current[section] = getSectionSnapshot(section, finalManual)
      })
      // Teachers saved by "Save and finish later" but not yet confirmed on Review Teachers: the
      // newer list, so it replaces the server's (see teachersDraftToRestore). "Already sent"
      // stays the server's list, so the next Continue sends the draft — unless it is the same.
      if (teachersDraft) {
        if (backendHasTeachers) {
          sentSectionSnapshotsRef.current.teachers = getSectionSnapshot('teachers', {
            ...finalManual,
            teachers: mappedAny.teachers,
            deletedTeachers: [],
          })
        }
        finalManual.teachers = teachersDraft.teachers
        finalManual.teachersCount = teachersDraft.teachers.length
        finalManual.deletedTeachers = teachersDraft.deletedTeachers ?? []
        // Already in preferences: the draft effect must not write it again.
        lastSavedTeachersDraftRef.current = getSectionSnapshot('teachers', finalManual)
      }
      // Deletes queued on Academic Structure before the user left: the backend still holds
      // what they name (they go out on that step's Next), so hide it again and keep the
      // queue. Applied AFTER the snapshots above were seeded from the pre-delete data, so the
      // classes checkpoint sees a change and actually sends the queue.
      return {...prev, manualData: applyPendingDeletes(finalManual, classesDraft)}
    })
  }, [])

  // A returning user whose resume point is gone (preferences unreadable, or wiped after "Save and
  // finish later"; or the sample-data seed answered 409 because the school has its own data):
  // resume from what the server holds instead of step 1. Waits for GET onboarding/data. Only for
  // a bare /onboarding landing — a deep link keeps its URL. The position is not written to
  // furthestRankRef, so the forward-only pointer save stores it again once the step renders.
  const landedBareRef = useRef(!stepParam)
  const resumeFromServerDataRef = useRef(false)
  const tryResumeFromServerData = useCallback(() => {
    if (!resumeFromServerDataRef.current || !onboardingDataDoneRef.current) return
    resumeFromServerDataRef.current = false
    if (!landedBareRef.current) return
    const position = inferResumeFromOnboardingData(onboardingDataResultRef.current)
    if (!position) return
    navigate(buildOnboardingPath(position.stepSlug, position.subStepSlug), {replace: true})
  }, [navigate])
  const requestResumeFromServerData = useCallback(() => {
    resumeFromServerDataRef.current = true
    tryResumeFromServerData()
  }, [tryResumeFromServerData])

  // URL -> state: resolve /onboarding/<step>/<subStep> whenever the params change.
  useEffect(() => {
    const stepDef = getStepBySlug(stepParam)

    // Bare /onboarding or an unknown step: redirect to the saved step.
    if (!stepDef) {
      const current = stepForNumber(currentStep)
      navigate(buildOnboardingPath(current?.slug ?? 'timetable-type'), {replace: true})
      return
    }

    setCurrentStep(stepNumberForSlug(stepDef))
    setInitialCoreSetupSubStep(
      stepDef.slug === 'school-setup' ? coreSetupSubStepIndex(subStepParam) : undefined
    )
    // Academic Structure is a single review page: every old sub-step URL lands on it.
    setInitialManualTab(
      stepDef.slug === 'assign-subjects' ? assignSubjectsTab(subStepParam) : undefined
    )
    setInitialTeachersPhase(
      stepDef.slug === 'assign-subjects' ? assignSubjectsTeachersPhase(subStepParam) : undefined
    )
    setActiveSubStepSlug(stepDef.subSteps.length ? canonicalSubStepSlug(subStepParam) : null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepParam, subStepParam])

  // state -> URL: keep the URL in sync with the current step + active sub-step as the user
  // navigates. Skipped on initial mount: there the URL is the source of truth (handled
  // above), and currentStep may still hold its stale pre-resolution value.
  useEffect(() => {
    if (!writeBackReadyRef.current) {
      writeBackReadyRef.current = true
      return
    }
    const stepDef = stepForNumber(currentStep)
    if (!stepDef) return
    const subSlug = stepDef.subSteps.length ? activeSubStepSlug : null
    const desired = buildOnboardingPath(stepDef.slug, subSlug)
    if (location.pathname !== desired) {
      navigate(desired, {replace: true})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, activeSubStepSlug])

  /**
   * Entry decision for a user with no saved wizard progress. Runs at most once per browser
   * (`ONBOARDING_WELCOME_SEEN_KEY`): a brand-new admin school is seeded with sample data
   * automatically; everyone else (non-admin, sample data unavailable, or a tenant that just
   * cleared its sample data to set up for real) lands directly in the wizard.
   */
  const decideNewUserEntry = (prefs?: Record<string, any>) => {
    if (localStorage.getItem(ONBOARDING_WELCOME_SEEN_KEY) === 'true') return
    localStorage.setItem(ONBOARDING_WELCOME_SEEN_KEY, 'true')
    // The server's own record of a sample run (`OnboardingWizard.sampleData`). Logging out clears
    // localStorage, so the local flags alone would take a returning school for a brand-new one and
    // seed sample data into a tenant that already has data (the server refuses it).
    const serverSample = getOnboardingSampleDataPrefs(prefs)
    if (serverSample?.active === true) return
    const sampleDataCleared =
      localStorage.getItem(ONBOARDING_SAMPLE_DATA_CLEARED_KEY) === 'true' ||
      serverSample?.active === false
    const sampleDataUnavailable =
      localStorage.getItem(ONBOARDING_SAMPLE_DATA_UNAVAILABLE_KEY) === 'true'
    // Never auto-seed from a support session: impersonating a brand-new tenant must not
    // create sample data (or grant credits) in their workspace with zero clicks.
    const canAutoSeed =
      !sampleDataCleared && !sampleDataUnavailable && isAdmin() && !isSupportSession
    if (canAutoSeed) {
      // Show the seeding loader immediately so step 1 doesn't flash before the effect fires.
      setIsSeedingSampleData(true)
      setIsGenerating(true)
      setAutoSeedRequested(true)
      return
    }
    if (sampleDataCleared) {
      // Confirm the clear before dropping into step 1; the actual-data tracking and the flag
      // reset happen when the user continues from that screen.
      setShowSampleClearedScreen(true)
      return
    }
    trackActualDataStart()
    clearOnboardingSampleDataClearedFlag()
  }

  const trackActualDataStart = () => {
    if (isSupportSession) return
    // ONE ordered batch, not two racing POSTs: the funnel writer synthesises
    // earlier substeps from arrival order, so when onboarding_started lands
    // first the genuine data_selection row is discarded as a backfill dupe
    // (ONBOARDING_BULK_EVENTS spec §2 names exactly this pair).
    trackOnboardingEvents(dispatch, urddId, [
      {event_name: 'data_selection', substep: 'actual_data'},
      {
        event_name: 'onboarding',
        substep: 'onboarding_started',
        metadata: {data_type: 'actual_data', device_type: detectDeviceType()},
      },
    ])
  }

  const handleSampleClearedContinue = () => {
    trackActualDataStart()
    clearOnboardingSampleDataClearedFlag()
    setShowSampleClearedScreen(false)
  }

  // Resume from preferences on mount: fetch the saved position, restore mode/importType so
  // step resolution + manual-only guards work, and (only when landing on bare /onboarding)
  // navigate straight to where the user left off. Deep links keep their explicit URL.
  useEffect(() => {
    if (resumeStartedRef.current) return
    resumeStartedRef.current = true
    const landedBare = !stepParam
    dispatch(
      getUserPreferences(
        (res: any) => {
          // Not an object when the stored value no longer parses (node-api returns the raw text):
          // later saves start from {} rather than spreading that string.
          const prefs = readablePreferences(res?.result) ?? {}
          const resumePointLost = lostResumePoint(res?.result)
          preferencesRef.current = prefs
          onboardingCurriculumConfigRef.current =
            (prefs?.OnboardingWizard?.curriculum as OnboardingCurriculumConfig) ?? null
          onboardingSetupRef.current = (prefs?.OnboardingWizard?.setup as OnboardingSetup) ?? null
          // Already-persisted setup fields count as touched: the effect writes the bucket whole,
          // so without this the user's next edit would save just that one field and drop the rest.
          Object.keys(onboardingSetupRef.current ?? {}).forEach((field) =>
            touchedSetupFieldsRef.current.add(field)
          )
          onboardingClassesDraftRef.current =
            (prefs?.OnboardingWizard?.classesDraft as OnboardingClassesDraft) ?? null
          onboardingTeachersDraftRef.current = prefs?.OnboardingWizard?.teachersDraft ?? null
          onboardingTeachersConfirmedAtRef.current =
            prefs?.OnboardingWizard?.teachersConfirmedAt ?? null
          const progress = prefs?.OnboardingWizard?.progress
          // Where "Save and finish later" left the user.
          const savedResumeAt = prefs?.OnboardingWizard?.resumeAt
          // Restore the mode from its dedicated bucket (saved the moment it's chosen), falling
          // back to the progress pointer. Kept outside the stepSlug gate so a mode picked on
          // step 1 is pre-filled even if the user left before advancing.
          const savedMode =
            (prefs?.OnboardingWizard?.mode as WizardData['mode']) ?? progress?.mode ?? null
          const savedCountry = (prefs?.OnboardingWizard?.country as string | undefined) ?? null
          if (savedMode || savedCountry) {
            setData((prev) => ({
              ...prev,
              ...(savedMode ? {mode: savedMode} : {}),
              ...(savedCountry ? {country: savedCountry} : {}),
            }))
          }
          if (progress?.stepSlug) {
            furthestRankRef.current = onboardingPositionRank(
              progress.stepSlug,
              progress.subStepSlug
            )
            // Backfill the derived fields onto a pointer saved before they existed. The forward-only
            // save below only fires when the user advances, so a stalled user — exactly who the
            // nudge email targets — would otherwise never get a progressPercent stored.
            const savedPercent = onboardingProgressPercent(progress.stepSlug, progress.subStepSlug)
            const savedLabel = onboardingStepLabel(progress.stepSlug)
            if (
              savedPercent !== null &&
              (progress.progressPercent !== savedPercent || progress.stepLabel !== savedLabel)
            ) {
              const backfilled = {
                ...prefs,
                OnboardingWizard: {
                  ...(prefs?.OnboardingWizard ?? {}),
                  progress: {
                    ...progress,
                    progressPercent: savedPercent,
                    stepLabel: savedLabel,
                  },
                },
              }
              preferencesRef.current = backfilled
              if (!isSupportSession) {
                dispatch(
                  updateUserPreferences(
                    {preferences: backfilled},
                    () => {},
                    () => {}
                  ) as any
                )
              }
            }
            if (landedBare) {
              // "Save and finish later" stores where the user was; it wins over the
              // furthest-reached pointer when it is not ahead of it.
              const resumeAt = savedResumeAt
              const resumeRank = resumeAt?.stepSlug
                ? onboardingPositionRank(
                    canonicalStepSlug(resumeAt.stepSlug) ?? resumeAt.stepSlug,
                    resumeAt.subStepSlug ?? null
                  )
                : -1
              const target =
                resumeRank >= 0 && resumeRank <= furthestRankRef.current ? resumeAt : progress
              // Normalize: pointers saved before the step-slug rename carry old slugs.
              navigate(
                buildOnboardingPath(
                  canonicalStepSlug(target.stepSlug) ?? target.stepSlug,
                  target.subStepSlug ?? null
                ),
                {replace: true}
              )
            }
          }
          // No progress pointer but a "Save and finish later" point: resume there (it is a saved
          // position too, and the furthest one known).
          if (!progress?.stepSlug && savedResumeAt?.stepSlug) {
            const resumeSlug = canonicalStepSlug(savedResumeAt.stepSlug) ?? savedResumeAt.stepSlug
            const resumeSub = savedResumeAt.subStepSlug ?? null
            const resumeRank = onboardingPositionRank(resumeSlug, resumeSub)
            if (resumeRank >= 0) {
              furthestRankRef.current = resumeRank
              if (landedBare) {
                navigate(buildOnboardingPath(resumeSlug, resumeSub), {replace: true})
              }
            }
          }
          // Resuming into a saved position never re-enters the new-user flow (persist the flag so
          // it can't happen on a later visit either); otherwise decide the entry once.
          if (progress?.stepSlug || savedResumeAt?.stepSlug) {
            localStorage.setItem(ONBOARDING_WELCOME_SEEN_KEY, 'true')
          } else if (resumePointLost) {
            // Been here before, but the saved position did not survive: never the new-user
            // entry (it auto-seeds sample data), resume from the school's saved data instead.
            localStorage.setItem(ONBOARDING_WELCOME_SEEN_KEY, 'true')
            requestResumeFromServerData()
          } else {
            decideNewUserEntry(prefs)
          }
          setPrefsLoaded(true)
          prefsDoneRef.current = true
          tryHydrateFromServer()
        },
        () => {
          preferencesRef.current = {}
          // Couldn't determine resume state; treat as a new user per the localStorage flag.
          decideNewUserEntry()
          setPrefsLoaded(true)
          prefsDoneRef.current = true
          tryHydrateFromServer()
        }
      ) as any
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Re-fetch onboarding data after the Lesson Planning Excel import so the imported entities
  // show up in the wizard grids without a reload. Re-runs the same mapper the mount hydration
  // uses; `hydratedFromServerRef` is reset so tryHydrateFromServer runs again.
  const refreshAfterExcelImport = useCallback(() => {
    setIsHydrating(true)
    // The mapper re-associates the returned levels with their curriculum through this copy of
    // the saved config, which was read once on mount: for a school that picked its curriculum
    // in this visit it is still null (or the placeholder), and the re-hydration would relabel
    // every level "Default Curriculum". The live wizard state is the current truth.
    const liveConfig = extractCurriculumConfig(data.manualData)
    if (hasRealCurriculum(liveConfig)) onboardingCurriculumConfigRef.current = liveConfig
    // Same for the setup bucket (students, venues, timings…): the mount-time copy is spread over
    // the live state on re-hydration, so students entered in this visit would be replaced by
    // whatever preferences held when the page loaded.
    onboardingSetupRef.current = extractOnboardingSetup(data.manualData)
    dispatch(
      getOnboardingData(
        (res: any) => {
          onboardingDataResultRef.current = res
          onboardingDataDoneRef.current = true
          hydratedFromServerRef.current = false
          seedAllSectionsRef.current = true
          tryHydrateFromServer()
        },
        () => {
          setIsHydrating(false)
        }
      ) as any
    )
  }, [data.manualData, dispatch, tryHydrateFromServer])

  // Server-side data prefill: on mount, fetch the user's previously-saved onboarding data so
  // completed steps come back pre-filled (even on a fresh device where localStorage is empty).
  // Fires once; the response is mapped into WizardData.manualData via setData in onSuccess.
  useEffect(() => {
    if (onboardingDataFetchedRef.current) return
    onboardingDataFetchedRef.current = true
    dispatch(
      getOnboardingData(
        (res: any) => {
          // Stash the raw response; the mapper joins it with the curriculum config from
          // preferences and runs once both have resolved (see tryHydrateFromServer).
          onboardingDataResultRef.current = res
          onboardingDataDoneRef.current = true
          tryHydrateFromServer()
          tryResumeFromServerData()
        },
        () => {
          // No saved onboarding data: still hydrate the setup sections restored from preferences.
          onboardingDataDoneRef.current = true
          tryHydrateFromServer()
          tryResumeFromServerData()
        }
      ) as any
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // state -> preferences: save forward progress whenever the active step/sub-step advances
  // past the furthest point reached. Gated on `prefsLoaded` so the first save merges onto the
  // fetched preferences rather than clobbering them; re-runs when prefs finish loading to
  // capture a position reached before the GET resolved.
  useEffect(() => {
    if (!prefsLoaded) return
    // THE Bug-B write: rendering a step ahead of the saved position persists it.
    if (isSupportSession) return
    const stepDef = stepForNumber(currentStep)
    if (!stepDef) return
    const subSlug = stepDef.subSteps.length ? activeSubStepSlug : null
    const rank = onboardingPositionRank(stepDef.slug, subSlug)
    if (rank < 0 || rank <= furthestRankRef.current) return
    furthestRankRef.current = rank

    // ADVANCING PAST A SCREEN IS THE USER CONFIRMING IT.
    //
    // `touchedSetupFieldsRef` deliberately holds only fields the user EDITED, so a value the
    // wizard pre-filled never reads as "they saved this screen" (see the guard's comment
    // above). That is right for a screen merely viewed, and wrong for one walked past: a user
    // who keeps the default "Main Branch" and clicks Next has completed Branches, but touched
    // nothing, so the funnel never counted them. The Next button is disabled until the screen
    // is valid, which makes the advance itself the evidence — the same rule node-api applies
    // server-side in `resolveSavedSignals`.
    //
    // Fields of the position they are ON are NOT included: they may have opened it and
    // stopped, and that is the drop-off the card exists to show.
    setupFieldsPassedBefore(rank).forEach((field) => touchedSetupFieldsRef.current.add(field))

    // Carried in the SAME write as the pointer. The setup effect below only fires on a
    // `data.manualData` change, and confirming a pre-filled screen changes nothing, so
    // without this the newly-confirmed fields would sit unsent until the next real edit.
    const confirmedSetup = extractOnboardingSetup(data.manualData, touchedSetupFieldsRef.current)
    // Advancing usually changes `data.manualData` in the same commit (the Next handlers
    // re-commit edits), which also fires the setup effect below. Record what this write
    // carries so that effect sees nothing new and skips — otherwise two preference PUTs
    // left ~1 ms apart, both carrying this pointer, and node-api derived every
    // pointer-based analytics row twice, racing past its dedupe.
    if (Object.keys(confirmedSetup).length > 0) {
      lastSavedSetupRef.current = JSON.stringify(confirmedSetup)
    }

    const base = preferencesRef.current ?? {}
    const merged = {
      ...base,
      OnboardingWizard: {
        ...(base?.OnboardingWizard ?? {}),
        ...(Object.keys(confirmedSetup).length > 0 ? {setup: confirmedSetup} : {}),
        progress: {
          stepSlug: stepDef.slug,
          subStepSlug: subSlug,
          // The analytics funnel's own name for this position. The wizard states it so the
          // server does not have to re-derive it from a mirrored copy of the step tree —
          // that copy fell behind twice in one week and took the whole linear funnel down
          // with it. See onboardingPositionName.
          positionName: onboardingPositionName(stepDef.slug, subSlug),
          importType: data.importType ?? null,
          mode: data.mode ?? null,
          // Sub-step-level completion + the exact sidebar label, for the backend's onboarding
          // nudge email. Only the wizard knows the step tree, so it computes both here and the
          // email renders them verbatim (see onboardingSections).
          progressPercent: onboardingProgressPercent(stepDef.slug, subSlug),
          stepLabel: onboardingStepLabel(stepDef.slug),
        },
      },
    }
    preferencesRef.current = merged
    dispatch(
      updateUserPreferences(
        {preferences: merged},
        () => {},
        () => {}
      ) as any
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, activeSubStepSlug, prefsLoaded])

  // Analytics `reached` for Mode Selection — the ONE linear position the preferences pointer
  // cannot witness.
  //
  // The effect above writes the pointer the moment `prefsLoaded` flips, with `currentStep`
  // still at its initial 1 and `furthestRankRef` at -1. That write happens on MOUNT, while
  // the Welcome modal is painted over the wizard and the user has clicked nothing — so
  // deriving reach from it ticked Mode Selection for every tenant that landed on
  // /onboarding, including the sample-data-only ones who never saw the screen and never
  // fired `onboarding_started`. (Reported 13 Aug 2026 for tenant `loaks`: Onboarding Started
  // cross, Mode Selection tick.) node-api now drops a pointer that never left position 1;
  // this is what replaces it, and it can only fire once the screen is genuinely on screen.
  //
  // Positions 2–13 keep their server-derived reach: the pointer only gets there by the user
  // advancing, which is exactly the fact those rows claim. Fire-and-forget; the server
  // dedupes repeats per (tenant, sub-step, wizard run).
  useEffect(() => {
    if (!prefsLoaded || isSupportSession) return
    // Not visible: cleared-confirmation screen on top, sample data seeding, or a full-screen overlay.
    if (showSampleClearedScreen || isSeedingSampleData || isGenerating || isAutofillLoading) return
    if (stepForNumber(currentStep)?.slug !== 'timetable-type') return
    trackOnboardingEvent(dispatch, urddId, 'onboarding_substep', 'mode', {
      import_type: data.importType ?? null,
      mode: data.mode ?? null,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    prefsLoaded,
    showSampleClearedScreen,
    isSeedingSampleData,
    isGenerating,
    isAutofillLoading,
    currentStep,
  ])

  // Analytics `reached` for the Summary screen (step 5): it sits past the linear flow the
  // preferences pointer covers, so it is emitted explicitly like the Assign Subjects tabs.
  // Fire-and-forget; the server dedupes repeats.
  useEffect(() => {
    if (currentStep !== 5 || isSupportSession) return
    trackOnboardingEvent(dispatch, urddId, 'onboarding_substep_final', 'summary', {
      import_type: data.importType,
      mode: data.mode,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep])

  // state -> preferences: persist the curriculum config (curriculums + level layout) whenever it
  // changes. GET /onboarding/data carries no curriculum, so this saved config is what lets us
  // re-associate the returned levels with their curriculum on resume (see mapOnboardingData).
  //
  // Gated on the server hydration having run, NOT on a user edit (`hydratingRef`): a curriculum
  // the wizard prefilled and the user merely accepted has to be saved too, or a resume at a
  // later step comes back without it and falls back to the placeholder.
  useEffect(() => {
    if (!prefsLoaded || !hydratedFromServerRef.current || isSupportSession) return
    const config = extractCurriculumConfig(data.manualData)
    if (
      config.curriculums.length === 0 &&
      (config.customCurriculums ?? []).length === 0 &&
      Object.keys(config.levelsByCurriculum).length === 0
    ) {
      return
    }
    // Never replace a real saved curriculum with the placeholder a hydration without the
    // saved config produces — that made the loss permanent across reloads.
    if (
      isPlaceholderCurriculumConfig(config) &&
      hasRealCurriculum(preferencesRef.current?.OnboardingWizard?.curriculum)
    ) {
      return
    }
    const serialized = JSON.stringify(config)
    if (serialized === lastSavedCurriculumRef.current) return
    lastSavedCurriculumRef.current = serialized
    // Keep the mapper's copy current: a re-hydration (Excel import) must see what was saved.
    onboardingCurriculumConfigRef.current = config

    const base = preferencesRef.current ?? {}
    const merged = {
      ...base,
      OnboardingWizard: {
        ...(base?.OnboardingWizard ?? {}),
        curriculum: config,
      },
    }
    preferencesRef.current = merged
    dispatch(
      updateUserPreferences(
        {preferences: merged},
        () => {},
        () => {}
      ) as any
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    data.manualData.curriculums,
    data.manualData.curriculumCodes,
    data.manualData.curriculumShortCodes,
    data.manualData.customCurriculums,
    data.manualData.institutionPrefillKey,
    data.manualData.levelsByCurriculum,
    prefsLoaded,
  ])

  // state -> preferences: persist the non-API onboarding sections (duration/timings/venues/
  // frequency/students) so they too come back pre-filled on resume. GET /onboarding/data does not
  // return these, so preferences is their source of truth (see extractOnboardingSetup).
  //
  // Debounced: the bucket holds every student, so saving on each change sent the whole list
  // (and serialized it) per keystroke. The save reads the latest state when it fires and merges
  // onto the latest preferences, so the other buckets written in between are kept.
  const latestManualDataRef = useRef(data.manualData)
  useEffect(() => {
    latestManualDataRef.current = data.manualData
  })
  const setupSaveTimerRef = useRef<number | null>(null)
  const saveSetupNow = useCallback(() => {
    if (setupSaveTimerRef.current !== null) {
      window.clearTimeout(setupSaveTimerRef.current)
      setupSaveTimerRef.current = null
    }
    if (hydratingRef.current || isSupportSession) return
    const setup = extractOnboardingSetup(latestManualDataRef.current, touchedSetupFieldsRef.current)
    if (Object.keys(setup).length === 0) return
    const serialized = JSON.stringify(setup)
    if (serialized === lastSavedSetupRef.current) return
    lastSavedSetupRef.current = serialized

    const base = preferencesRef.current ?? {}
    const merged = {
      ...base,
      OnboardingWizard: {
        ...(base?.OnboardingWizard ?? {}),
        setup,
      },
    }
    preferencesRef.current = merged
    dispatch(
      updateUserPreferences(
        {preferences: merged},
        () => {},
        () => {}
      ) as any
    )
  }, [dispatch, isSupportSession])
  useEffect(() => {
    if (!prefsLoaded || hydratingRef.current || isSupportSession) return
    if (setupSaveTimerRef.current !== null) window.clearTimeout(setupSaveTimerRef.current)
    setupSaveTimerRef.current = window.setTimeout(saveSetupNow, SETUP_SAVE_DEBOUNCE_MS)
  }, [data.manualData, isSupportSession, prefsLoaded, saveSetupNow])
  // A save still waiting when the user leaves (tab closed, wizard left) goes out right away.
  useEffect(() => {
    const flush = () => {
      if (setupSaveTimerRef.current !== null) saveSetupNow()
    }
    window.addEventListener('pagehide', flush)
    return () => {
      window.removeEventListener('pagehide', flush)
      flush()
    }
  }, [saveSetupNow])

  // state -> preferences: persist the selected mode (class vs subject) the moment it's chosen, in
  // its own bucket. The progress pointer only captures mode when the position advances past step 1,
  // so this ensures the choice comes back pre-filled even if the user leaves before clicking Next.
  useEffect(() => {
    if (!prefsLoaded || hydratingRef.current || isSupportSession) return
    if (!data.mode) return
    const base = preferencesRef.current ?? {}
    if (base?.OnboardingWizard?.mode === data.mode) return
    const merged = {
      ...base,
      OnboardingWizard: {
        ...(base?.OnboardingWizard ?? {}),
        mode: data.mode,
      },
    }
    preferencesRef.current = merged
    dispatch(
      updateUserPreferences(
        {preferences: merged},
        () => {},
        () => {}
      ) as any
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.mode, prefsLoaded])

  // state -> preferences: persist the Setup Basics country the moment it's set (detected or picked),
  // mirroring the mode bucket, so it comes back pre-filled and later steps can key defaults off it.
  // Deliberately NOT gated on `hydratingRef`: a detected country is a prefill write, so with that
  // gate it never persisted, and a resume landing past Setup Basics had no country — the
  // Institution Setup review then read "<no country>|<code>" as a new prefill basis and replaced
  // the saved hours/session/breaks. A country restored from preferences equals the stored one
  // and short-circuits below, so this still never re-writes hydrated state.
  useEffect(() => {
    if (!prefsLoaded || isSupportSession) return
    if (!data.country) return
    const base = preferencesRef.current ?? {}
    if (base?.OnboardingWizard?.country === data.country) return
    const merged = {
      ...base,
      OnboardingWizard: {
        ...(base?.OnboardingWizard ?? {}),
        country: data.country,
      },
    }
    preferencesRef.current = merged
    dispatch(
      updateUserPreferences(
        {preferences: merged},
        () => {},
        () => {}
      ) as any
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.country, prefsLoaded])

  // state -> preferences: draft-save the in-progress Classes sub-step (class list + section config
  // + flow position). Classes only reach the backend after the Review sub-step, so a user who fills
  // classes and leaves at the Sections sub-step would otherwise lose them on resume. This draft is a
  // FALLBACK source; the backend still wins on resume whenever it returns classes (see
  // tryHydrateFromServer + mergeClassesDraft).
  useEffect(() => {
    if (!prefsLoaded || hydratingRef.current || isSupportSession) return
    const draft = extractClassesDraft(data.manualData)
    if (Object.keys(draft).length === 0) return
    const serialized = JSON.stringify(draft)
    if (serialized === lastSavedClassesDraftRef.current) return
    lastSavedClassesDraftRef.current = serialized

    const base = preferencesRef.current ?? {}
    const merged = {
      ...base,
      OnboardingWizard: {
        ...(base?.OnboardingWizard ?? {}),
        classesDraft: draft,
      },
    }
    preferencesRef.current = merged
    dispatch(
      updateUserPreferences(
        {preferences: merged},
        () => {},
        () => {}
      ) as any
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.manualData, prefsLoaded])

  // state -> preferences: the teachers draft (OnboardingWizard.teachersDraft, see
  // utils/teachersDraft.ts). Kept while the list differs from what the last teachers import
  // sent; removed once that import succeeds, with `teachersConfirmedAt` saying when.
  //
  // `OnboardingWizard` with the draft brought in line with `manual`, or null when it already is.
  // Only once the server hydration has run: before it, "already sent" is not known yet.
  const withTeachersDraft = useCallback(
    (wizard: Record<string, any>, manual: WizardData['manualData']) => {
      if (!hydratedFromServerRef.current) return null
      const snapshot = getSectionSnapshot('teachers', manual)
      const sent = sentSectionSnapshotsRef.current.teachers ?? EMPTY_TEACHERS_SNAPSHOT
      if (snapshot === sent) {
        lastSavedTeachersDraftRef.current = null
        if (!wizard?.teachersDraft) return null
        // A re-hydration (Excel import) must not bring the sent draft back either.
        onboardingTeachersDraftRef.current = null
        const {teachersDraft: _sent, ...rest} = wizard
        return {...rest, teachersConfirmedAt: new Date().toISOString()}
      }
      if (snapshot === lastSavedTeachersDraftRef.current) return null
      lastSavedTeachersDraftRef.current = snapshot
      return {...wizard, teachersDraft: buildTeachersDraft(manual, new Date().toISOString())}
    },
    []
  )
  const teachersDraftTimerRef = useRef<number | null>(null)
  const saveTeachersDraftNow = useCallback(() => {
    if (teachersDraftTimerRef.current !== null) {
      window.clearTimeout(teachersDraftTimerRef.current)
      teachersDraftTimerRef.current = null
    }
    if (isSupportSession) return
    const base = preferencesRef.current ?? {}
    const wizard = withTeachersDraft(base?.OnboardingWizard ?? {}, latestManualDataRef.current)
    if (!wizard) return
    const merged = {...base, OnboardingWizard: wizard}
    preferencesRef.current = merged
    dispatch(
      updateUserPreferences(
        {preferences: merged},
        () => {},
        () => {}
      ) as any
    )
  }, [dispatch, isSupportSession, withTeachersDraft])
  useEffect(() => {
    if (!prefsLoaded || !hydratedFromServerRef.current || isSupportSession) return
    const unsent =
      getSectionSnapshot('teachers', data.manualData) !==
      (sentSectionSnapshotsRef.current.teachers ?? EMPTY_TEACHERS_SNAPSHOT)
    // Just sent: drop the draft at once, so a logout right after Continue cannot restore it
    // over the server's list (its new rows would then be created a second time).
    if (!unsent) {
      saveTeachersDraftNow()
      return
    }
    if (hydratingRef.current) return
    // Debounced: Review Teachers edits the list per keystroke.
    if (teachersDraftTimerRef.current !== null) window.clearTimeout(teachersDraftTimerRef.current)
    teachersDraftTimerRef.current = window.setTimeout(saveTeachersDraftNow, SETUP_SAVE_DEBOUNCE_MS)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the teachers section only
  }, [data.manualData.teachers, data.manualData.deletedTeachers, prefsLoaded, isSupportSession])
  useEffect(() => {
    const flush = () => {
      if (teachersDraftTimerRef.current !== null) saveTeachersDraftNow()
    }
    window.addEventListener('pagehide', flush)
    return () => {
      window.removeEventListener('pagehide', flush)
      flush()
    }
  }, [saveTeachersDraftNow])

  // Steps:
  // 1: Mode
  // 2: Basic Setup (Branches, Session, Venues, Timings, Frequency)
  // 3: Curriculum (Curriculum, Levels, Classes, Sections, Review, Subjects)
  // 4: Manual Data Input (Teachers/Lesson Planning/Students)
  // 5: Summary
  const maxStep = 5
  const handleNext = () => {
    if (currentStep < maxStep) setCurrentStep((c) => c + 1)
  }

  const handleBack = () => {
    if (isGenerating) return
    if (currentStep <= 1) return
    const targetStep = currentStep - 1
    // Back resumes the previous step at its LAST sub-step so the user walks the
    // sub-steps backwards continuously instead of restarting the step from its start.
    const targetDef = stepForNumber(targetStep)
    const lastSubStep = targetDef?.subSteps.length
      ? targetDef.subSteps[targetDef.subSteps.length - 1]
      : null
    if (targetDef?.slug === 'school-setup') {
      // Step 2 is a single review page — Back lands on the review, not a sub-step modal.
      setInitialCoreSetupSubStep(undefined)
    } else if (targetDef?.slug === 'assign-subjects') {
      setInitialManualTab(assignSubjectsTab(lastSubStep ?? undefined))
      setInitialTeachersPhase(assignSubjectsTeachersPhase(lastSubStep ?? undefined))
    }
    // Seed the URL slug too so the URL-sync effects settle on the right sub-step
    // immediately instead of briefly writing the outgoing step's slug. Steps 2 and 3
    // are single review pages with no sub-step URL (see Step3AcademicStructure's mount
    // effect for why Step 3 must not sit on a real sub-step).
    setActiveSubStepSlug(
      targetDef?.slug === 'school-setup' || targetDef?.slug === 'academic-structure'
        ? null
        : lastSubStep
    )
    setCurrentStep(targetStep)
  }

  const getTimetablePath = useCallback((manualData: WizardData['manualData']) => {
    const branchId = manualData?.selectedTimetableBranchId
    const levelId = manualData?.selectedTimetableLevelId
    const durationId = manualData?.selectedTimetableDurationId
    if (branchId && levelId && durationId) {
      const params = new URLSearchParams({
        branchId: String(branchId),
        levelId: String(levelId),
        durationId: String(durationId),
      })
      return `/institute/schedule/timetable?${params.toString()}`
    }
    return '/institute/schedule/timetable'
  }, [])

  // helper removed: getEffectiveTimetableIds (unused)

  const completeOnboarding = useCallback(
    (
      redirectPath: string,
      options?: {
        sampleData?: {levelId: number; durationId: number; branchId: number}
        /**
         * Also POST onboarding/complete before leaving. Only the sample-data path sets
         * this: the manual wizard records completion when Lesson Planning is finished
         * (handleManualDataComplete), not on the Summary CTAs.
         */
        markComplete?: boolean
      }
    ) => {
      if (data.importType === 'manual') {
        localStorage.setItem('onboarding_manual_data', JSON.stringify(data.manualData))
      }

      setIsGenerating(true)

      const finalizeOnboarding = () => {
        // The completion flags stay off in a support session: they'd persist in the
        // OPERATOR's browser for this tenant origin and route every later impersonation
        // straight to the dashboard (legacyOnboardingComplete), skipping resume for good.
        if (!isSupportSession) {
          localStorage.setItem('onboarding_complete', 'true')
          if (data.importType) {
            localStorage.setItem('onboarding_import_type', data.importType)
          }
        }
        if (storageKey) localStorage.removeItem(storageKey)
        clearStoredSchoolSystemDetails()
        clearStoredCurriculumPrefills()
        // Full page load avoids Metronic layout corruption from WizardLayout unmount + SPA navigate.
        window.location.assign(redirectPath)
      }

      // A support session leaving the wizard must not fire the completion event or write
      // the tutorial/sampleData preference buckets for the tenant — navigate only.
      if (isSupportSession) {
        finalizeOnboarding()
        return
      }

      // The real completion signal (POST onboarding/complete). For the manual wizard it
      // has already been sent when Lesson Planning was finished (handleManualDataComplete),
      // so the Summary CTAs only navigate; the sample-data path has no Lesson Planning and
      // sends it here. Fire-and-forget — navigation proceeds whether or not it lands.
      const recordCompletionEvent = () => {
        if (!options?.markComplete) {
          finalizeOnboarding()
          return
        }
        dispatch(
          markOnboardingComplete(
            () => {
              finalizeOnboarding()
            },
            () => {
              finalizeOnboarding()
            }
          ) as any
        )
      }

      // Completion used to also PATCH general-settings with `onboarding_complete`, but no such
      // column exists on the general_settings table, so the call could only ever fail. The
      // onboarding/complete endpoint above is the real signal; localStorage carries the flag
      // the route guards read.

      const mergeAndUpdate = (existing: any = {}) => {
        const onboardingWizard: Record<string, unknown> = {
          ...(existing?.OnboardingWizard ?? {}),
          tutorial: {
            ...(existing?.OnboardingWizard?.tutorial ?? {}),
            dashboard: false,
          },
        }
        if (options?.sampleData) {
          onboardingWizard.sampleData = {
            active: true,
            levelId: options.sampleData.levelId,
            durationId: options.sampleData.durationId,
            branchId: options.sampleData.branchId,
          }
        }
        const merged = {
          ...existing,
          OnboardingWizard: onboardingWizard,
        }
        dispatch(
          updateUserPreferences(
            {preferences: merged},
            () => recordCompletionEvent(),
            () => recordCompletionEvent()
          ) as any
        )
      }
      dispatch(
        getUserPreferences(
          (res: any) => mergeAndUpdate(res?.result ?? {}),
          () => mergeAndUpdate({})
        ) as any
      )
    },
    [data.importType, data.manualData, dispatch, isSupportSession, storageKey]
  )

  const handleGenerateFirstTimetable = useCallback(() => {
    // Fire-and-forget; the completion chain runs a few requests before the full
    // page load, which gives this one time to land. The server dedupes repeats.
    if (!isSupportSession) trackOnboardingEvent(dispatch, urddId, 'dashboard', 'first_opened')
    completeOnboarding(getTimetablePath(data.manualData))
  }, [completeOnboarding, data.manualData, dispatch, getTimetablePath, isSupportSession, urddId])

  const handleGoToDashboard = useCallback(() => {
    if (!isSupportSession) trackOnboardingEvent(dispatch, urddId, 'dashboard', 'first_opened')
    completeOnboarding('/dashboard')
  }, [completeOnboarding, dispatch, isSupportSession, urddId])

  /**
   * "Save and finish later" (Teacher Allocation handover, Point 5). Reuses the wizard's own
   * saving: the open screen's pending edits are flushed into the wizard data, the Teacher
   * Allocation grid (client flow only; the server flow saves every edit) is synced with the
   * regular bulk import (never a pending AI proposal — those live in Versi until applied), and
   * the current position is stored in preferences so the next sign-in resumes there. Earlier steps already save their data to preferences as it
   * changes. The confirmation appears only once everything has succeeded.
   */
  const handleSaveAndFinishLater = () => {
    if (saveLaterStatus === 'saving') return
    setSaveLaterStatus('saving')
    const flushed =
      currentStep === 4
        ? step4FlushRef.current?.()
        : currentStep === 3
          ? step3FlushRef.current?.()
          : undefined
    const manualData = flushed ?? data.manualData
    const stepDef = stepForNumber(currentStep)
    const activeSubSlug = stepDef?.subSteps.length ? activeSubStepSlug : null
    // Add Teachers hands what was typed or uploaded to Review on the flush above (Step4Manual):
    // resume there, where the list shows. The entry screen would open with an empty field.
    const subSlug =
      currentStep === 4 && activeSubSlug === 'teachers' && (manualData.teachers ?? []).length > 0
        ? 'review-teachers'
        : activeSubSlug
    // Academic Structure: the same classes and subjects sync as its Next, so levels added on the
    // page reach the server and a resume (which reads the server) brings them back.
    // Teacher Allocation: only the grid is synced from here — Teachers and Students have their
    // own confirm steps that validate first. Until then students are kept in the setup bucket
    // and teachers in the teachers draft, written with the resume point below (localStorage
    // alone does not survive a logout). With the server-side allocation every cell edit is
    // already saved, and pushing this copy would overwrite them (allocation handoff §7, §9.1).
    const fields =
      currentStep === 3
        ? ['classes', 'subjects']
        : currentStep === 4 && subSlug === 'lesson-planning' && !allocationServerEnabled()
          ? ['subjects']
          : []

    const waitForIdleSync = () =>
      new Promise<void>((resolve, reject) => {
        const started = Date.now()
        const check = () => {
          if (!syncInFlightRef.current) resolve()
          else if (Date.now() - started > 20000) reject(new Error('sync busy'))
          else window.setTimeout(check, 250)
        }
        check()
      })
    const syncStep = () =>
      fields.length === 0
        ? Promise.resolve()
        : waitForIdleSync().then(
            () =>
              new Promise<void>((resolve, reject) =>
                syncIncrementalData(
                  {...data, manualData},
                  fields as any,
                  manualData,
                  () => resolve(),
                  (err) => reject(err)
                )
              )
          )
    const writeResumePoint = () =>
      new Promise<void>((resolve, reject) => {
        if (isSupportSession || !stepDef) {
          resolve()
          return
        }
        const base = preferencesRef.current ?? {}
        const wizard = base?.OnboardingWizard ?? {}
        // The teachers draft rides in this write, so "All changes saved" waits for it too.
        if (teachersDraftTimerRef.current !== null) {
          window.clearTimeout(teachersDraftTimerRef.current)
          teachersDraftTimerRef.current = null
        }
        const merged = {
          ...base,
          OnboardingWizard: {
            ...(withTeachersDraft(wizard, manualData) ?? wizard),
            resumeAt: {
              stepSlug: stepDef.slug,
              subStepSlug: subSlug,
              savedAt: new Date().toISOString(),
            },
          },
        }
        dispatch(
          updateUserPreferences(
            {preferences: merged},
            () => {
              preferencesRef.current = merged
              resolve()
            },
            () => {
              // Not stored: the next save must carry the draft again.
              lastSavedTeachersDraftRef.current = null
              reject(new Error('preferences'))
            }
          ) as any
        )
      })

    // Server-side allocation (AI servers): the save is confirmed by POST onboarding/save-progress
    // after the resume point, and "All changes saved" waits for it (allocation handoff §7).
    const confirmServerSave = () =>
      !allocationServerEnabled() || isSupportSession
        ? Promise.resolve()
        : new Promise<void>((resolve, reject) =>
            dispatch(
              requestSaveOnboardingProgress(
                undefined,
                () => resolve(),
                () => reject(new Error('save-progress'))
              ) as any
            )
          )

    syncStep()
      .then(writeResumePoint)
      .then(confirmServerSave)
      .then(() => {
        savedAtRef.current = Date.now()
        setSaveLaterStatus('saved')
        setSaveLaterModalOpen(true)
      })
      .catch(() => setSaveLaterStatus('error'))
  }

  // "All changes saved" describes the last save: the next edit clears it. The save's own
  // writes (ids merged back from the server) land right after it and are ignored.
  useEffect(() => {
    if (saveLaterStatus !== 'saved') return
    if (Date.now() - savedAtRef.current < 2000) return
    setSaveLaterStatus('idle')
    // eslint-disable-next-line react-hooks/exhaustive-deps -- react to data edits only
  }, [data])

  const syncIncrementalData = (
    wizardData: WizardData,
    fields: Array<keyof ReturnType<typeof buildBulkImportPayload>>,
    manualDataOverride?: WizardData['manualData'],
    onSuccess?: (manualData?: WizardData['manualData']) => void,
    onError?: (err: any) => void,
    syncOptions?: OnboardingSyncOptions
  ) => {
    const effectiveManualData = manualDataOverride ?? wizardData.manualData
    const sectionsOnly = syncOptions?.sectionsOnly
    const eligibleSections = getSectionsForFields(fields as string[]).filter(
      (section) => !sectionsOnly || sectionsOnly.includes(section)
    )
    const forceAllocationSections: IncrementalSectionKey[] = [
      'branch',
      'teachers',
      'classes',
      'subjects',
    ]
    const sectionsToSend = eligibleSections.filter((section) => {
      if (!hasSectionData(section, effectiveManualData)) return false
      if (syncOptions?.forceAllocationSync && forceAllocationSections.includes(section)) {
        return true
      }
      const nextSnapshot = getSectionSnapshot(section, effectiveManualData)
      return sentSectionSnapshotsRef.current[section] !== nextSnapshot
    })

    if (sectionsToSend.length === 0) {
      onSuccess?.(effectiveManualData)
      return
    }
    // One save at a time: the snapshots above are still the ones from before the save in flight.
    if (syncInFlightRef.current) return

    setIsGenerating(true)
    const fullPayload = buildBulkImportPayload(effectiveManualData)
    type BulkImportPayload = ReturnType<typeof buildBulkImportPayload>
    const payload: Partial<BulkImportPayload> = {}
    const payloadKeys = new Set<keyof BulkImportPayload>()
    sectionsToSend.forEach((section) => {
      SECTION_TO_PAYLOAD_KEYS[section].forEach((key) => payloadKeys.add(key))
    })

    if (
      payloadKeys.has('timings') ||
      payloadKeys.has('break_timings') ||
      payloadKeys.has('teachers') ||
      payloadKeys.has('classes') ||
      payloadKeys.has('subjects') ||
      payloadKeys.has('students')
    ) {
      payloadKeys.add('branch')
    }

    if (payloadKeys.has('classes') && effectiveManualData.venuesSameForClass) {
      payloadKeys.add('venues')
    }

    payloadKeys.forEach((key) => {
      ;(payload as Record<string, unknown>)[key as string] = fullPayload[key]
    })

    // Which screen this save belongs to. node-api used to infer "Lesson Planning saved"
    // (and from it, onboarding completed) from any `subjects` row carrying a teacher, so a
    // Students-tab save that re-posted a half-filled grid completed tenants who never
    // finished the tab. The backend now emits the Lesson Planning rows only for the
    // 'lessonPlanning' checkpoint — see docs/backend-specs/
    // onboarding-lesson-planning-completion-inference.md.
    const fieldNames = fields as string[]
    const checkpoint = fieldNames.includes('lessonPlanning')
      ? 'lessonPlanning'
      : fieldNames.includes('students')
        ? 'students'
        : fieldNames.includes('teachers')
          ? 'teachers'
          : fieldNames.includes('classes') || fieldNames.includes('subjects')
            ? 'classes'
            : 'setup'
    ;(payload as Record<string, unknown>).checkpoint = checkpoint
    // A Students save carries subjects only so its students' subjects exist: the ones not saved
    // yet. Saved ones would go as `update` rows with this copy's teacher — a copy that may predate
    // an allocation import (every teacher empty), which cleared the school's assignments. Those
    // rows are the Lesson Planning save's to send, so their snapshot is not taken here either.
    const studentsSave = checkpoint === 'students'
    // Server-side Teacher Allocation holds each saved subject's teacher, frequency and duration
    // (saved per cell on onboarding/allocation), so this copy's values for those rows are stale -
    // usually empty. As `update` rows they cleared the school's assignments: the allocation
    // template download and an Academic Structure edit both wiped every teacher that way. Only
    // the subjects not saved yet go, as on a Students save.
    const newSubjectsOnly = studentsSave || allocationServerEnabled()
    if (newSubjectsOnly && Array.isArray(payload.subjects)) {
      // Deletes go too: a subject removed from a level has an id, and only its delete row
      // takes it (and its teacher allocation) off the server.
      const unsaved = (payload.subjects as any[]).filter(
        (entry: any) =>
          entry?.action === 'delete' ||
          (!parsePositiveNumber(entry?.id) && entry?.action !== 'update')
      )
      // None new: no `subjects` key at all, rather than an empty list.
      if (unsaved.length > 0) payload.subjects = unsaved as typeof payload.subjects
      else delete payload.subjects
    }
    // AI server only: validates students against the onboarding classes/subjects and clears a
    // deleted class's grade/section once nothing else uses them. Non-AI servers ignore it.
    ;(payload as Record<string, unknown>).source = 'onboarding'

    syncInFlightRef.current = true
    dispatch(
      bulkCsvImport(
        payload,
        (res: any) => {
          const reportedErrors =
            res?.errors ?? res?.result?.errors ?? res?.data?.errors ?? res?.result?.data?.errors
          // "Teacher/Student not found for deletion": it is already gone (deleted from the grid
          // earlier), which is what the save wanted. Not a reason to stop the step.
          const responseErrors = Array.isArray(reportedErrors)
            ? reportedErrors.filter(
                (entry: any) =>
                  !/not found for deletion/i.test(String(entry?.error || entry?.message || ''))
              )
            : reportedErrors
          if (Array.isArray(responseErrors) && responseErrors.length > 0) {
            syncInFlightRef.current = false
            setIsGenerating(false)
            const firstError = responseErrors[0]
            const message =
              firstError?.error || firstError?.message || 'Failed to save onboarding data.'
            // Each student error names a different student (the backend rejects a bad row
            // whole and still saves the rest), so list them instead of only the first.
            const studentErrorMessages = responseErrors
              .filter((entry: any) => entry?.entity === 'student')
              .map((entry: any) => String(entry?.error || entry?.message || '').trim())
              .filter(Boolean)
            if (studentErrorMessages.length > 1) {
              const lines = studentErrorMessages.slice(0, 5)
              if (studentErrorMessages.length > 5) {
                lines.push(
                  intl.formatMessage(
                    {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.STUDENTS.MORE_ROWS'},
                    {count: studentErrorMessages.length - 5}
                  )
                )
              }
              toast.error(<div style={{whiteSpace: 'pre-line'}}>{lines.join('\n')}</div>, {
                autoClose: 12000,
              })
            } else {
              toast.error(message)
            }
            onError?.({message, errors: responseErrors, raw: res})
            return
          }

          // The sent snapshots are taken in `finish` below, from the manualData the wizard
          // actually keeps after this response — not from `effectiveManualData`. The response
          // merges server ids into the rows and the delete queues are consumed, so a snapshot
          // of the pre-response data never matched the state again and the next sync re-sent
          // every one of these sections untouched.

          // Extract venue IDs returned by the API and build a className → venueId map.
          // Venue names follow the pattern "Venue N (ClassName)" when venuesSameForClass is true,
          // so we parse out the class name from each venue and persist the mapping for use as
          // roomId when sending subjects.
          const venueIdResult = (() => {
            if (!payloadKeys.has('venues')) return null
            const src = res?.result ?? res?.data ?? res ?? {}
            const created = src?.created ?? src?.result?.created ?? {}
            const updated = src?.updated ?? src?.result?.updated ?? {}
            const rawVenues: any[] = [
              ...(Array.isArray(created?.venues) ? created.venues : []),
              ...(Array.isArray(updated?.venues) ? updated.venues : []),
              ...(Array.isArray(src?.venues) ? src.venues : []),
            ]
            if (!rawVenues.length) return null
            const byClassName = new Map<string, number>()
            const byVenueName = new Map<string, number>()
            rawVenues.forEach((v: any) => {
              const id = parsePositiveNumber(v?.id)
              const name = String(v?.name ?? '').trim()
              if (!id || !name) return
              if (!byVenueName.has(name)) byVenueName.set(name, id)
              const m = name.match(/\(([^)]+)\)\s*$/)
              if (!m) return
              const className = m[1].trim()
              if (className && !byClassName.has(className)) byClassName.set(className, id)
            })
            return byClassName.size > 0 || byVenueName.size > 0 ? {byClassName, byVenueName} : null
          })()

          const parsed = extractTimetableSelections(res)
          let nextManualData = applyBulkImportResponseToManualData(
            effectiveManualData,
            res,
            payloadKeys,
            parsed
          )

          // Delete entries are a pending command queue. Once the classes request succeeds,
          // consume the queue so a later unrelated sync cannot delete the same records again.
          if (payloadKeys.has('classes')) {
            nextManualData = {...nextManualData, deletedClasses: []}
          }
          // Same for students: the deletes went through, and every row on the grid now exists in
          // the school, so removing one later must delete it too.
          if (payloadKeys.has('teachers')) {
            nextManualData = {...nextManualData, deletedTeachers: []}
          }
          // Subject deletes rode this request (the newSubjectsOnly filter keeps them).
          if (payloadKeys.has('subjects')) {
            nextManualData = consumeDeletedSubjects(nextManualData)
          }
          if (payloadKeys.has('students')) {
            nextManualData = {
              ...nextManualData,
              deletedStudents: [],
              students: (nextManualData.students ?? []).map((student) => ({
                ...student,
                _saved: true,
              })),
            }
          }

          if (venueIdResult) {
            const {byClassName, byVenueName} = venueIdResult
            const withVenues: any = {...nextManualData}
            if (byClassName.size > 0) {
              const prevMap = (withVenues.venueIdByClassName as Record<string, number>) ?? {}
              const merged: Record<string, number> = {...prevMap}
              byClassName.forEach((id, className) => {
                merged[className] = id
              })
              withVenues.venueIdByClassName = merged
            }
            if (byVenueName.size > 0) {
              const prevMap = (withVenues.venueIdByName as Record<string, number>) ?? {}
              const merged: Record<string, number> = {...prevMap}
              byVenueName.forEach((id, name) => {
                merged[name] = id
              })
              withVenues.venueIdByName = merged
              withVenues.venues = (effectiveManualData.venues ?? []).map((v: any) => {
                if (parsePositiveNumber(v?.id)) return v
                const name = String(v?.name ?? '').trim()
                const id = byVenueName.get(name)
                return id ? {...v, id} : v
              })
            }
            nextManualData = withVenues
          }

          // Cleared once the snapshots are taken, which may wait for the level deletes below.
          const finish = (manual: WizardData['manualData']) => {
            sectionsToSend.forEach((section) => {
              // Only the new subjects went (see above): the rest are still to send.
              if (studentsSave && section === 'subjects') return
              sentSectionSnapshotsRef.current[section] = getSectionSnapshot(section, manual)
            })
            syncInFlightRef.current = false
            setData((prev) => ({...prev, manualData: manual}))
            setIsGenerating(false)
            onSuccess?.(manual)
          }

          // Removed branches go last, in their own request: node-api drops the whole branch
          // (classes, levels, timetable data, sections) at once, so nothing else may still
          // address it. "Branch not found for deletion" means it is already gone. A failure
          // keeps the queue for the next sync; the requests before it have succeeded either way.
          const deleteBranches = (manual: WizardData['manualData']) => {
            const branchDeletes = buildBranchDeletePayload(manual)
            if (branchDeletes.length === 0) {
              finish(consumeDeletedBranches(manual))
              return
            }
            dispatch(
              bulkCsvImport(
                {branch: branchDeletes, source: 'onboarding'},
                (branchRes: any) => {
                  const branchErrors =
                    branchRes?.errors ??
                    branchRes?.result?.errors ??
                    branchRes?.data?.errors ??
                    branchRes?.result?.data?.errors
                  const realErrors = (Array.isArray(branchErrors) ? branchErrors : []).filter(
                    (entry: any) =>
                      !/not found/i.test(String(entry?.error || entry?.message || ''))
                  )
                  if (realErrors.length > 0) {
                    toast.error(
                      realErrors[0]?.error ||
                        realErrors[0]?.message ||
                        intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.BRANCH_DELETE_FAILED'})
                    )
                    finish(manual)
                    return
                  }
                  finish(consumeDeletedBranches(manual))
                },
                (err: any) => {
                  toast.error(
                    err?.message ||
                      intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.BRANCH_DELETE_FAILED'})
                  )
                  finish(manual)
                }
              ) as any
            )
          }

          // Removed levels go out in their OWN request, after the classes one has landed: the
          // backend resolves classes by name + level, so a level must be emptied before it can
          // be deleted, and the branch payload above only ever lists the levels that still
          // exist. A level nothing can address (never synced, no id) just leaves the queue.
          const levelDeletes = payloadKeys.has('classes')
            ? buildLevelDeletePayload(nextManualData)
            : []
          if (levelDeletes.length === 0) {
            if (payloadKeys.has('classes') && (nextManualData.deletedLevels ?? []).length > 0) {
              nextManualData = consumeDeletedLevels(nextManualData)
            }
            deleteBranches(nextManualData)
            return
          }
          const queuedManual = nextManualData
          dispatch(
            bulkCsvImport(
              // source: 'onboarding' lets the AI server clean up before deleting a level: it
              // refuses a level that still has classes, moves its teachers/staff to another level
              // of the branch, and clears its permission rows, grades and timetable data (without
              // that, FKs blocked every level delete/rename). Non-AI servers ignore the field.
              // The main request above carries the flag too, but only this request deletes
              // levels - do not add level deletes elsewhere.
              {branch: levelDeletes, source: 'onboarding'},
              (levelRes: any) => {
                const levelErrors =
                  levelRes?.errors ??
                  levelRes?.result?.errors ??
                  levelRes?.data?.errors ??
                  levelRes?.result?.data?.errors
                // "not found for deletion" is benign here: the level was already cascaded
                // away or never existed server-side. Anything else keeps the queue so the
                // next sync retries; the classes request has already succeeded either way.
                const realErrors = (Array.isArray(levelErrors) ? levelErrors : []).filter(
                  (entry: any) => !/not found/i.test(String(entry?.error || entry?.message || ''))
                )
                if (realErrors.length > 0) {
                  toast.error(
                    realErrors[0]?.error ||
                      realErrors[0]?.message ||
                      'Failed to delete the removed levels.'
                  )
                  deleteBranches(queuedManual)
                  return
                }
                deleteBranches(consumeDeletedLevels(queuedManual))
              },
              (err: any) => {
                toast.error(err?.message || 'Failed to delete the removed levels.')
                deleteBranches(queuedManual)
              }
            ) as any
          )
        },
        (err: any) => {
          syncInFlightRef.current = false
          setIsGenerating(false)
          toast.error(err?.message || 'Failed to save onboarding data. Please try again.')
          onError?.(err)
        }
      ) as any
    )
  }

  // const runBulkImport = (
  //   wizardData: WizardData,
  //   onSuccess: () => void,
  //   onError: (err: any) => void
  // ) => {
  //   setIsGenerating(true)
  //   const payload = buildBulkImportPayload(wizardData.manualData)
  //   dispatch(
  //     bulkCsvImport(
  //       payload,
  //       (res: any) => {
  //         const parsed = extractTimetableSelections(res)
  //         if (parsed.branchOptions.length > 0 || parsed.levelOptions.length > 0) {
  //           setData((prev) => ({
  //             ...prev,
  //             manualData: {
  //               ...prev.manualData,
  //               timetableBranchOptions:
  //                 parsed.branchOptions.length > 0
  //                   ? parsed.branchOptions
  //                   : (prev.manualData.timetableBranchOptions ?? []),
  //               timetableLevelOptions:
  //                 parsed.levelOptions.length > 0
  //                   ? parsed.levelOptions
  //                   : (prev.manualData.timetableLevelOptions ?? []),
  //               timetableDurationOptions: parsed.durationOptions,
  //               selectedTimetableBranchId:
  //                 parsed.selectedBranchId ?? prev.manualData.selectedTimetableBranchId ?? null,
  //               selectedTimetableLevelId:
  //                 parsed.selectedLevelId ?? prev.manualData.selectedTimetableLevelId ?? null,
  //               selectedTimetableDurationId:
  //                 parsed.selectedDurationId ?? prev.manualData.selectedTimetableDurationId ?? null,
  //             },
  //           }))
  //         }
  //         setIsGenerating(false)
  //         onSuccess()
  //       },
  //       (err: any) => {
  //         setIsGenerating(false)
  //         onError(err)
  //       }
  //     ) as any
  //   )
  // }

  /**
   * The `onboarding_substep_edited` rows for whichever of `positions` the user actually edited
   * (ONBOARDING_EDIT_TRACKING spec). Appended LAST to the completion batch of the Next click
   * that completes them, and ONLY there: Next is disabled until the step is valid, so every
   * edit row lands beside its completion row and the backend's
   * `edited + not_edited == completed` identity holds on every row. Never flush this on
   * unmount or visibilitychange; those would be edits for screens nobody finished, and the
   * backend drops them. Repeats come back `deduped`, so Back-then-Next needs no bookkeeping.
   */
  const editedIn = (positions: readonly string[]): OnboardingBatchEvent[] =>
    positions
      .filter((position) => editedPositionsRef.current.has(position))
      .map((substep) => ({
        event_name: 'onboarding_substep_edited' as const,
        substep: substep as OnboardingBatchEvent['substep'],
      }))

  // Step 4 follows the same actual-diff rule as the earlier steps: a position's edit row is
  // sent only when the user changed THAT screen's data (teachers on Add Teachers, teacher rows
  // on Review Teachers, students, lesson-planning cells), never for merely passing through it.
  // Each tab's rows ride on the click that leaves that tab; repeats come back `deduped`.
  // The two Teachers screens report on the click that LEAVES them, not on open (decision of
  // 9 Sep 2026): Add Teachers -> Review posts `assign_teachers`, Review -> Continue posts
  // `assign_review_teachers`. Opening the tab (tab switch, resume, Back) posts nothing.
  const handleTeacherReview = () => {
    if (isSupportSession) return
    // The edit row is sent UNCONDITIONALLY beside the reach row (decision of 9 Sep 2026), not
    // via editedIn: Add Teachers is never prefilled, so reaching Review means the user entered
    // teachers — the same rule the subscription service applies read-side
    // (ALWAYS_EDITED_SUBSTEPS in OnboardingMainStepFunnel.js). The diff-based flag would also
    // miss the rows just added, which are still a deferred write at this point (Step4Manual
    // commits them via a microtask). The server dedupes repeats.
    // Order is data: reach, then saved, then edited — the same order node-api's bulk import
    // emits (reach BEFORE saved), and the edit row last so it lands beside its completion.
    // The saved row is posted from here (9 Sep 2026) because the analytics tick — and with it
    // the edited flag — keys on `onboarding_substep_saved`, which node-api only emits on the
    // teachers bulk import, i.e. on Review's Continue; until then the Add Teachers edit was
    // parked as "edited without completion".
    const metadata = {import_type: data.importType, mode: data.mode}
    trackOnboardingEvents(dispatch, urddId, [
      {event_name: 'onboarding_substep_final', substep: 'assign_teachers', metadata},
      {event_name: 'onboarding_substep_saved', substep: 'assign_teachers', metadata},
      {event_name: 'onboarding_substep_edited', substep: 'assign_teachers'},
    ])
  }

  const handleTeacherReviewConfirm = () => {
    if (isSupportSession) return
    // NOTE: the subscription service's `onboarding_substep_saved` whitelist currently lists
    // assign_teachers but NOT assign_review_teachers (only its `_final` and `_edited` events
    // were extended by the rename migration), so the review saved row below is rejected as an
    // unknown substep until that whitelist is extended backend-side. Sent anyway so the row
    // starts landing the moment the whitelist changes; a rejected item does not fail the batch.
    const metadata = {import_type: data.importType, mode: data.mode}
    trackOnboardingEvents(dispatch, urddId, [
      {event_name: 'onboarding_substep_final', substep: 'assign_review_teachers', metadata},
      {event_name: 'onboarding_substep_saved', substep: 'assign_review_teachers', metadata},
      ...editedIn(['assign_teachers', 'assign_review_teachers']),
    ])
  }

  /**
   * Students -> Lesson Planning: the Students tab is complete once the wizard has moved past
   * it (its Next syncs first, then switches the tab), so its edit row rides on that switch.
   */
  const handleStudentsPassed = () => {
    if (isSupportSession) return
    trackOnboardingEvents(dispatch, urddId, editedIn(['assign_students']))
  }

  const handleStep2Completion = () => {
    // The Institution Setup review page hosts what used to be five separate
    // sub-step screens; Next is disabled until every section is valid, so
    // passing it completes all five at once. The preferences pointer never
    // sits on the individual positions any more, so the funnel writer cannot
    // witness them from the pointer — this ordered batch is the witness
    // (ONBOARDING_BULK_EVENTS spec). Order = the funnel order of the positions
    // this page owns. (`curriculum_curriculum` is NOT here: its picker lives on
    // Setup Basics and rides in that page's batch.)
    if (!isSupportSession) {
      trackOnboardingEvents(dispatch, urddId, [
        {event_name: 'onboarding_substep', substep: 'core_branches'},
        {event_name: 'onboarding_substep', substep: 'core_duration'},
        {event_name: 'onboarding_substep', substep: 'core_frequency'},
        {event_name: 'onboarding_substep', substep: 'core_venues'},
        {event_name: 'onboarding_substep', substep: 'core_timings'},
        // Edit rows last, one per position the user changed on this page.
        ...editedIn(positionsCompletedByStep(2)),
      ])
    }
    handleNext()
  }

  const handleStep3Completion = () => {
    // Same shape as Step 2: the Academic Structure review page replaces the old
    // Levels/Classes/Sections/Review/Subjects screens, and Next is disabled until every
    // level is complete, so passing it completes those positions at once. The pointer
    // sits on the bare step (analytics name `curriculum_curriculum`, already completed by
    // the Setup Basics batch — any real sub-step would make node-api's ordinal backfill
    // credit positions on mere render), so this ordered batch is the funnel's only
    // witness for them. `curriculum_review` is deliberately NOT in it (decision of
    // 9 Sep 2026: the review is not tracked) — it has no edit row either, see
    // editedPositionsIn.
    if (!isSupportSession) {
      trackOnboardingEvents(dispatch, urddId, [
        {event_name: 'onboarding_substep', substep: 'curriculum_levels'},
        {event_name: 'onboarding_substep', substep: 'curriculum_classes'},
        {event_name: 'onboarding_substep', substep: 'curriculum_sections'},
        {event_name: 'onboarding_substep', substep: 'curriculum_subjects'},
        // Edit rows last, one per position the user changed on this page.
        ...editedIn(positionsCompletedByStep(3)),
      ])
    }
    handleNext()
  }

  const handleManualDataComplete = () => {
    // Lesson Planning finished (Step4Manual only calls this after its last tab has synced):
    // this is where the wizard is genuinely done, so POST onboarding/complete here rather
    // than on the Summary CTAs — a user who reaches the Summary and closes the tab has
    // still completed setup. Fire-and-forget; the server dedupes repeats, so Back + Next
    // is harmless. Support sessions never write completion for the tenant.
    if (!isSupportSession) {
      // Edit rows for whichever Step 4 screens the user actually changed. The Teachers and
      // Students rows were already sent when those tabs were left; the server dedupes them.
      trackOnboardingEvents(dispatch, urddId, editedIn(positionsCompletedByStep(4)))
      dispatch(
        markOnboardingComplete(
          () => {},
          () => {}
        ) as any
      )
    }
    handleNext()
  }

  /**
   * The single funnel every step component writes wizard state through.
   *
   * `options.prefill` marks a write the WIZARD made on the user's behalf (a screen default seeded
   * on arrival), not one the user made. Those must not end hydration and must not mark a setup
   * field as touched, or they land in `OnboardingWizard.setup` and the backend records the
   * sub-step as saved at view time — permanently, since each (tenant, sub-step) row is written
   * once. Everything else is a real edit.
   */
  const updateData = useCallback((newData: Partial<WizardData>, options?: UpdateDataOptions) => {
    const isUserEdit = !options?.prefill
    if (isUserEdit) hydratingRef.current = false
    setData((prev) => {
      if (isUserEdit) {
        editedPositionsIn(prev, newData, currentStepRef.current, options?.subStep).forEach(
          (position) => editedPositionsRef.current.add(position)
        )
      }
      if (newData.manualData) {
        if (isUserEdit) {
          // Step components spread the whole manualData into every call, so compare by value:
          // only fields that actually changed count as touched.
          const next = newData.manualData as Record<string, unknown>
          const before = prev.manualData as Record<string, unknown>
          ONBOARDING_SETUP_FIELDS.forEach((field) => {
            if (!(field in next)) return
            // Already touched: nothing to learn, and comparing a large list by value per
            // keystroke is what made the Students grid lag.
            if (touchedSetupFieldsRef.current.has(field)) return
            if (next[field] === before[field]) return
            if (JSON.stringify(next[field] ?? null) === JSON.stringify(before[field] ?? null)) {
              return
            }
            touchedSetupFieldsRef.current.add(field)
          })
        }
        return {
          ...prev,
          ...newData,
          manualData: {
            ...prev.manualData,
            ...newData.manualData,
          },
        }
      }
      return {...prev, ...newData}
    })
  }, [])

  const isStepValid = useMemo(() => {
    // Setup Basics requires a timetable mode and at least one curriculum — the
    // curriculum picker no longer exists on Academic Structure, so this is the
    // only place to choose one (custom names enter via the "Other" option).
    if (currentStep === 1) {
      return data.mode !== null && (data.manualData?.curriculums?.length ?? 0) > 0
    }
    return true
  }, [currentStep, data.mode, data.manualData?.curriculums])

  const steps = useMemo(() => {
    const base = [
      {
        id: 1,
        label: intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP1.TITLE'}),
        desc: intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP1.DESC'}),
      },
      {
        id: 2,
        label: intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TITLE'}),
        desc: intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.DESC'}),
      },
      {
        id: 3,
        label: intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP3.TITLE'}),
        desc: intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP3.DESC'}),
      },
      {
        id: 4,
        label: intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP5.TITLE'}),
        desc: intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP5.DESC'}),
      },
    ]
    return base
  }, [intl])

  // Browser tab title. Onboarding renders outside the main layout and the app only sets
  // document.title from the general-settings reducer, so each step names the tab itself.
  useEffect(() => {
    const rawTabTitle = generalSettings?.tabTitle
    const tabTitle = typeof rawTabTitle === 'string' ? rawTabTitle.trim() : ''
    const baseTitle = isSelectiveAiDomains() ? 'Da1Timetable' : tabTitle || 'Ilmversity'
    // Step 5 (the un-numbered completion state) still names the tab.
    const stepLabel =
      currentStep === 5
        ? intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP6.TITLE'})
        : steps.find((s) => s.id === currentStep)?.label
    document.title = stepLabel ? `${stepLabel} - ${baseTitle}` : baseTitle
    return () => {
      document.title = baseTitle
    }
  }, [steps, currentStep, generalSettings, intl])

  const sidebarStepLabels = useMemo(
    () => [
      intl.formatMessage({id: 'ONBOARDING_WIZARD.SIDEBAR.STEP1'}),
      intl.formatMessage({id: 'ONBOARDING_WIZARD.SIDEBAR.STEP2'}),
      intl.formatMessage({id: 'ONBOARDING_WIZARD.SIDEBAR.STEP3'}),
      intl.formatMessage({id: 'ONBOARDING_WIZARD.SIDEBAR.STEP5'}),
    ],
    [intl]
  )

  const {mode} = useThemeMode()
  const currentTheme = mode === 'system' ? systemMode : mode
  const isDark = currentTheme === 'dark'

  const getThemeClass = () => {
    let baseTheme = ''
    if (currentStep === 1) baseTheme = 'onboarding-theme-step1'
    else if (currentStep === 2) baseTheme = 'onboarding-theme-step2'
    else if (currentStep === 3) baseTheme = 'onboarding-theme-step3'
    else if (currentStep === 4) baseTheme = 'onboarding-theme-step4'
    else if (currentStep === 5) baseTheme = 'onboarding-theme-step4'
    else baseTheme = 'onboarding-theme-step3'

    if (isDark) {
      baseTheme += ' onboarding-theme-dark'
    }

    return baseTheme
  }

  const handleModeSelectionNext = () => {
    if (!data.mode) return

    // Setup Basics done: report the positions completed on THIS page, in the
    // order its UI presents them — mode, then the curriculum picker. The
    // visibility witness above already fired `mode` when the screen was shown;
    // the server collapses the repeat to `deduped`, and re-sending it here
    // stamps the completed value of `mode` at the moment the page was passed.
    //
    // NOTE the UI/funnel order divergence: the funnel seed has the core_*
    // positions before curriculum_curriculum, so this early curriculum event
    // makes the ordinal backfill synthesise the core rows before the
    // Institution Setup batch lands (confirmed with the backend as the intended
    // behaviour for the collapsed wizard).
    //
    // Sent from the save's success callback, not on the click: a failed mode save keeps
    // the user on this page, and the completion + `_saved` rows must not claim otherwise.
    const trackStep1Completion = () => {
      if (isSupportSession) return
      trackOnboardingEvents(dispatch, urddId, [
        {
          event_name: 'onboarding_substep',
          substep: 'mode',
          metadata: {import_type: data.importType ?? null, mode: data.mode ?? null},
        },
        {event_name: 'onboarding_substep', substep: 'curriculum_curriculum'},
        // The card ticks Curriculum on this `_saved` row, not the arrival row above. It
        // used to be derived only from a later screen's pointer flush (minutes late, or
        // never for a tenant who stops here). Order matters: arrival before saved.
        // pointer_index stays 1 — see the OnboardingEventName comment for why not 2.
        {
          event_name: 'onboarding_substep_saved',
          substep: 'curriculum_curriculum',
          metadata: {pointer_index: 1},
        },
        // Edit rows last, one per position the user changed on this page. `mode` is judged
        // by value (modeSelectionEdited), not by editedIn: it is picked before this page's
        // other edits and saved on the spot, so a reload in between left no edit behind.
        ...(modeSelectionEdited({mode: data.mode, country: data.country}, defaultCountryRef.current)
          ? [{event_name: 'onboarding_substep_edited' as const, substep: 'mode' as const}]
          : []),
        ...editedIn(positionsCompletedByStep(1).filter((position) => position !== 'mode')),
      ])
    }

    // Advancing past a screen is the user confirming it (see the progress-pointer
    // effect). Step 1 can now be passed on pure prefills (class-wise + the first
    // recommended curriculums) with zero edits, and prefill writes keep
    // `hydratingRef` up, gating the curriculum-config persist effect — so without
    // this a zero-edit advance would never save the curriculums and a resume at a
    // later step would come back without them. Re-committing them as a user edit
    // (fresh array reference) clears the gate and re-fires that effect; the
    // touched-field tracking compares by value, so untouched setup fields stay
    // untouched and nothing is mis-stamped as saved.
    updateData({
      manualData: {
        ...data.manualData,
        curriculums: [...(data.manualData?.curriculums ?? [])],
      },
    })

    const subjectWiseSetting = data.mode === 'subject' ? 1 : 0
    setIsGenerating(true)
    dispatch(
      updateSubjectWiseSetting(
        {value: subjectWiseSetting},
        () => {
          setIsGenerating(false)
          trackStep1Completion()
          handleNext()
        },
        (err: any) => {
          setIsGenerating(false)
          toast.error(err?.message || 'Failed to save mode selection. Please try again.')
        }
      ) as any
    )
  }

  const canNavigateToSidebarStep = (targetStep: number) => {
    return !isGenerating && targetStep < currentStep
  }

  const handleSidebarStepClick = (targetStep: number) => {
    if (!canNavigateToSidebarStep(targetStep)) return
    setCurrentStep(targetStep)
  }

  const handleStartWithSampleData = useCallback(() => {
    if (!isSupportSession) {
      // Sample runs enter the funnel too (SAMPLE_DATA_ONBOARDING_TRACKING spec B2):
      // without this start row, sample schools read as a cliff after signup.
      // One ordered batch so the selection row can never lose the race against
      // the start row (ONBOARDING_BULK_EVENTS spec §2).
      trackOnboardingEvents(dispatch, urddId, [
        {event_name: 'data_selection', substep: 'sample_data'},
        {
          event_name: 'onboarding',
          substep: 'onboarding_started',
          metadata: {data_type: 'sample_data', device_type: detectDeviceType()},
        },
      ])
    }
    // Grant the default sample AI credits; fire-and-forget like available-services.
    // On success, queue the one-time "35 extra credits" popup for the dashboard.
    dispatch(addDefaultSampleCredits(() => markSampleCreditsPopupPending()))
    clearOnboardingSampleDataClearedFlag()
    setIsSeedingSampleData(true)
    setIsGenerating(true)

    const finishSampleDataRedirect = (branchId: number, levelId: number, durationId: number) => {
      localStorage.setItem('onboarding_import_type', 'sample_data')
      localStorage.setItem(ONBOARDING_WELCOME_SEEN_KEY, 'true')
      // Same signal the Summary CTAs fire (handleGoToDashboard). The sample
      // path never fired it, so sample schools had no terminal funnel step.
      // Only on a successful seed — a failed seed stays a genuine drop-off.
      if (!isSupportSession) trackOnboardingEvent(dispatch, urddId, 'dashboard', 'first_opened')
      completeOnboarding(buildSampleDataTimetablePath(branchId, levelId, durationId), {
        sampleData: {levelId, durationId, branchId},
        markComplete: true,
      })
    }

    // Any failure drops the user into the wizard underneath to set up their own data.
    const handleSeedFailure = (err: any) => {
      setIsSeedingSampleData(false)
      setIsGenerating(false)
      const statusCode = err?.statusCode

      if (statusCode === 409) {
        // The school already has its own data (the seed only ever runs on its own, for what
        // looked like a brand-new school): nothing went wrong for the user, so no error — they
        // simply carry on in the wizard, and sample data is not tried again. A school with data
        // has been through the wizard before (its resume point was lost), so it carries on from
        // that data rather than from step 1.
        localStorage.setItem(ONBOARDING_SAMPLE_DATA_UNAVAILABLE_KEY, 'true')
        requestResumeFromServerData()
        return
      }

      if (statusCode === 500) {
        const firstError = err?.result?.errors?.[0]?.error
        toast.error(firstError || err?.message || 'Sample data import completed with errors')
        return
      }

      toast.error(err?.message || 'Failed to seed sample data')
    }

    dispatch(
      seedOnboardingSampleData((res: any) => {
        const result = res?.result as SeedSampleDataResult | undefined
        const levelId = result?.levelId
        const durationId = result?.durationId

        if (!levelId || !durationId) {
          setIsSeedingSampleData(false)
          setIsGenerating(false)
          toast.error('Sample data was seeded but timetable IDs are missing.')
          return
        }

        dispatch(
          getBranches(
            {},
            (branchRes: any) => {
              const branchId = pickFirstBranchId(branchRes?.result)
              if (!branchId) {
                setIsSeedingSampleData(false)
                setIsGenerating(false)
                toast.error('Sample data was seeded but no branch was found.')
                return
              }
              finishSampleDataRedirect(branchId, levelId, durationId)
            },
            (branchErr: any) => {
              setIsSeedingSampleData(false)
              setIsGenerating(false)
              toast.error(
                branchErr?.message || 'Failed to load branches after seeding sample data.'
              )
            }
          ) as any
        )
      }, handleSeedFailure) as any
    )
  }, [completeOnboarding, dispatch, intl, isSupportSession, requestResumeFromServerData, urddId])

  // New-user auto-seed (see decideNewUserEntry). Lives here because the handler above is
  // declared after the resume effect that makes the decision.
  useEffect(() => {
    if (!autoSeedRequested) return
    setAutoSeedRequested(false)
    handleStartWithSampleData()
  }, [autoSeedRequested, handleStartWithSampleData])

  return (
    <div className='container-fluid p-0 h-100 onboarding-wizard-wrapper'>
      <div
        className={`onboarding-wizard-container ${getThemeClass()} ${sidebarCollapsed ? 'onboarding-sidebar-collapsed' : ''}`}
      >
        {/* Mobile Header Stepper - visible only on small screens */}
        <div className='onboarding-mobile-header'>
          <div className='onboarding-mobile-logo'>
            <img
              src={toAbsoluteUrl('media/auth/image 70.png')}
              alt=''
              className='onboarding-mobile-logo-img'
            />
            <span className='onboarding-mobile-logo-text'>TimeTable™</span>
          </div>
          <div className='onboarding-mobile-stepper'>
            {steps.map((step) => {
              const isClickable = canNavigateToSidebarStep(step.id)
              return (
                <div
                  key={step.id}
                  className={`onboarding-mobile-step ${currentStep === step.id ? 'onboarding-mobile-step-active' : ''} ${currentStep > step.id ? 'onboarding-mobile-step-completed' : ''} ${isClickable ? 'onboarding-mobile-step-clickable' : ''}`}
                  onClick={() => handleSidebarStepClick(step.id)}
                  onKeyDown={(e) => {
                    if ((e.key === 'Enter' || e.key === ' ') && isClickable) {
                      e.preventDefault()
                      handleSidebarStepClick(step.id)
                    }
                  }}
                  role={isClickable ? 'button' : undefined}
                  tabIndex={isClickable ? 0 : -1}
                  aria-disabled={!isClickable}
                >
                  <div className='onboarding-mobile-step-icon'>
                    {currentStep > step.id ? <Check size={14} strokeWidth={3} /> : step.id}
                  </div>
                  <span className='onboarding-mobile-step-label'>
                    {sidebarStepLabels[step.id - 1]}
                  </span>
                </div>
              )
            })}
          </div>
        </div>

        {/* Sidebar Stepper - hidden on small screens */}
        <div className='onboarding-sidebar'>
          <div className='onboarding-sidebar-logo'>
            <img
              src={toAbsoluteUrl('media/auth/image 70.png')}
              alt=''
              className='onboarding-sidebar-logo-img'
            />
            <span className='onboarding-sidebar-logo-text'>TimeTable™</span>
          </div>
          <div className='onboarding-sidebar-header'>
            <h3 className='onboarding-sidebar-title'>
              {intl.formatMessage({id: 'ONBOARDING_WIZARD.TITLE'})}
            </h3>
            <p className='onboarding-sidebar-subtitle'>
              {intl.formatMessage({id: 'ONBOARDING_WIZARD.SUBTITLE'})}
            </p>
          </div>
          <div className='onboarding-sidebar-separator' />
          <div className='onboarding-stepper'>
            {steps.map((step) => {
              const isClickable = canNavigateToSidebarStep(step.id)
              return (
                <div
                  key={step.id}
                  className={`onboarding-step ${currentStep === step.id ? 'onboarding-step-active' : ''} ${currentStep > step.id ? 'onboarding-step-completed' : ''} ${isClickable ? 'onboarding-step-clickable' : ''}`}
                  onClick={() => handleSidebarStepClick(step.id)}
                  onKeyDown={(event) => {
                    if ((event.key === 'Enter' || event.key === ' ') && isClickable) {
                      event.preventDefault()
                      handleSidebarStepClick(step.id)
                    }
                  }}
                  role={isClickable ? 'button' : undefined}
                  tabIndex={isClickable ? 0 : -1}
                  aria-disabled={!isClickable}
                >
                  <div className='onboarding-step-icon'>
                    {currentStep > step.id ? <Check size={22} strokeWidth={3} /> : step.id}
                  </div>
                  <div className='onboarding-step-label'>{sidebarStepLabels[step.id - 1]}</div>
                </div>
              )
            })}
          </div>
        </div>

        {/* Sidebar collapse/expand toggle - sits on the sidebar edge, hidden with it on mobile */}
        <button
          type='button'
          className={`onboarding-sidebar-toggle ${sidebarCollapsed ? 'active' : ''}`}
          onClick={() => setSidebarCollapsed((collapsed) => !collapsed)}
          aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-expanded={!sidebarCollapsed}
        >
          <ChevronLeft size={16} strokeWidth={2.5} />
        </button>

        <div className='onboarding-main-content' ref={mainContentRef}>
          {/* Step content waits for preferences: until they resolve we don't know whether to
              show the welcome screen or resume elsewhere, and rendering step 1 meanwhile
              flashes the mode-selection screen for new users. */}
          {prefsLoaded && currentStep <= 4 && !setupPaused && (
            <SaveAndFinishLaterAction
              status={saveLaterStatus}
              disabled={isGenerating || isHydrating}
              onSave={handleSaveAndFinishLater}
            />
          )}
          {prefsLoaded && setupPaused && (
            <SetupPausedScreen
              onResume={() => setSetupPaused(false)}
              onLogout={() => navigate('/logout')}
            />
          )}
          {prefsLoaded && !setupPaused && (
            <div key={currentStep} className='onboarding-step-body'>
              {currentStep === 1 && (
                <Step1Mode
                  data={data}
                  updateData={updateData}
                  isDark={isDark}
                  onNext={handleModeSelectionNext}
                  isNextDisabled={!isStepValid || isGenerating}
                  isHydrating={isHydrating}
                  onDefaultCountryResolved={handleDefaultCountryResolved}
                />
              )}
              {currentStep === 2 && (
                <Step2Review
                  data={data}
                  updateData={updateData}
                  isDark={isDark}
                  initialSubStep={initialCoreSetupSubStep}
                  // The review reports the section whose modal is open (null when back on
                  // the review); `duration` is the section the UI labels "Academic Session",
                  // so map it onto the URL slug before it becomes the address-bar path and
                  // the persisted resume pointer.
                  onActiveSubStepChange={(field) =>
                    setActiveSubStepSlug(field ? canonicalSubStepSlug(field) : null)
                  }
                  onNext={handleStep2Completion}
                  onBack={handleBack}
                  onPartialSubmit={(fields, manualData, cb, syncOptions, onError) =>
                    syncIncrementalData(data, fields as any, manualData, cb, onError, syncOptions)
                  }
                  isSubmitting={isGenerating}
                  isHydrating={isHydrating}
                />
              )}
              {currentStep === 3 && (
                <Step3AcademicStructure
                  data={data}
                  updateData={updateData}
                  isDark={isDark}
                  isHydrating={isHydrating}
                  onActiveSubStepChange={setActiveSubStepSlug}
                  onNext={handleStep3Completion}
                  onBack={handleBack}
                  onPartialSubmit={(fields, manualData, cb, syncOptions, onError) =>
                    syncIncrementalData(data, fields as any, manualData, cb, onError, syncOptions)
                  }
                  isSubmitting={isGenerating}
                  onAutofillLoadingChange={setIsAutofillLoading}
                  saveLaterFlushRef={step3FlushRef}
                />
              )}

              {currentStep === 4 && (
                <Step4Manual
                  saveLaterFlushRef={step4FlushRef}
                  data={data}
                  updateData={updateData}
                  isDark={isDark}
                  isHydrating={isHydrating}
                  onComplete={handleManualDataComplete}
                  onBack={handleBack}
                  onPartialSubmit={(fields, manualData, cb, syncOptions, onError) =>
                    syncIncrementalData(data, fields as any, manualData, cb, onError, syncOptions)
                  }
                  enabledTabs={
                    [
                      'teachers',
                      ...(data.mode === 'subject' ? (['students'] as const) : ([] as const)),
                      'lessonPlanning',
                    ] as const as any
                  }
                  initialTab={initialManualTab ?? 'teachers'}
                  initialTeachersPhase={initialTeachersPhase}
                  onTeacherReview={handleTeacherReview}
                  onTeacherReviewConfirm={handleTeacherReviewConfirm}
                  onExcelImported={refreshAfterExcelImport}
                  onActiveSubStepChange={(tabId) => {
                    if (activeSubStepSlug === 'students' && tabId === 'lessonPlanning') {
                      handleStudentsPassed()
                    }
                    setActiveSubStepSlug(assignSubjectsSlug(tabId) ?? null)
                    // Analytics `reached` for the branch-dependent tail: the preferences
                    // pointer only captures tab movement on the next save, so emit at the
                    // moment of the tab switch. Fire-and-forget; the server dedupes repeats.
                    // The two Teachers screens are deliberately absent: they report on the
                    // click that leaves them (handleTeacherReview / handleTeacherReviewConfirm),
                    // not on open. Their slugs still drive the URL and the resume pointer above.
                    const substep = (
                      {
                        students: 'assign_students',
                        lessonPlanning: 'assign_lesson_planning',
                      } as const
                    )[tabId as 'students' | 'lessonPlanning']
                    if (substep && !isSupportSession) {
                      trackOnboardingEvent(dispatch, urddId, 'onboarding_substep_final', substep, {
                        import_type: data.importType,
                        mode: data.mode,
                      })
                    }
                  }}
                />
              )}

              {currentStep === 5 && (
                <Step3SuccessSummary
                  data={data}
                  updateData={updateData}
                  isDark={isDark}
                  importType='manual'
                  primaryActionLabelId='ONBOARDING_WIZARD.STEP3_SUMMARY.BTN_GENERATE_FIRST_TIMETABLE'
                  onPrimaryAction={handleGenerateFirstTimetable}
                  secondaryActionLabelId='ONBOARDING_WIZARD.STEP3_SUMMARY.BTN_GO_TO_DASHBOARD'
                  onSecondaryAction={handleGoToDashboard}
                  isLoading={isGenerating}
                />
              )}
            </div>
          )}
        </div>
      </div>

      {showSampleClearedScreen && (
        <OnboardingWelcomeModal
          onStartSetup={handleSampleClearedContinue}
          onStartSampleData={() => undefined}
          showSampleDataOption={false}
          sampleDataCleared
        />
      )}

      <SaveAndFinishLaterModal
        open={saveLaterModalOpen}
        onKeepWorking={() => setSaveLaterModalOpen(false)}
        onExit={() => {
          setSaveLaterModalOpen(false)
          setSetupPaused(true)
        }}
      />

      {isSeedingSampleData && <OnboardingSampleDataLoader />}

      {(!prefsLoaded || isGenerating || isAutofillLoading) && !isSeedingSampleData && (
        <div className='onboarding-loading-overlay' role='status' aria-live='polite'>
          <img
            src={toAbsoluteUrl('media/logos/LogoAnim1.gif')}
            alt={intl.formatMessage({id: 'ONBOARDING_WIZARD.LOADING'})}
            className='onboarding-loading-logo'
          />
        </div>
      )}
    </div>
  )
}

export default OnboardingWizard
